import React, { useState, useEffect, useRef, useCallback } from 'react';
import useElementHeight from '../hooks/useElementHeight';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebNotification } from '../hooks/useWebNotification';
import { toast } from 'sonner';
import axios from 'axios';
import MediaPicker from '../components/MediaPicker';
import PinDurationPicker from '../components/PinDurationPicker';
import ViewOnceBubble from '../components/ViewOnceBubble';
import StatusViewer from '../components/StatusViewer';
import {
  connectSocket,
  disconnectSocket,
  joinRoom,
  leaveRoom,
  sendMessage as sendSocketMessage,
  sendTypingIndicator,
  markMessagesRead,
  onNewMessage,
  onUserTyping,
  onUserOnline,
  onUserOffline,
  onMessagesRead,
  onMessagesDelivered,
  onMessageUpdated,
  offNewMessage,
  offUserTyping,
  offUserOnline,
  offUserOffline,
  offMessagesRead,
  offMessagesDelivered,
  offMessageUpdated,
  onMessageDeleted,
  offMessageDeleted,
  onHeaderLabelsUpdated,
  offHeaderLabelsUpdated
} from '../utils/socket';
import { 
  PaperPlaneRight, 
  Image as ImageIcon, 
  Microphone,
  ArrowLeft,
  Trash,
  Copy,
  Bell,
  BellSlash,
  Smiley,
  DotsThreeVertical,
  Plus,
  ArrowBendUpLeft,
  ShareFat,
  X,
  User,
  UserCircle,
  Headset,
  Phone,
  FileText,
  WhatsappLogo,
  Link,
  CaretDown,
  Star,
  PushPin
} from '@phosphor-icons/react';
import VoiceMessage from '../components/VoiceMessage';
import ReadReceipts from '../components/ReadReceipts';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { idbSaveMessages, idbGetMessages, idbDeleteMessage, idbCleanupOld } from '../utils/idbCache';
import TypingIndicator from '../components/TypingIndicator';
import OnlineStatus from '../components/OnlineStatus';
import { onVisibilityFetch } from '../utils/pollingKeepAlive';
import WhatsAppInput from '../components/WhatsAppInput';
import ActionStickerBubble from '../components/ActionStickerBubble';
import ClientHistoryModal from '../components/ClientHistoryModal';
import ThemeToggleMenuItem from '../components/ThemeToggleMenuItem';
import AudioCallScreen from '../components/AudioCallScreen';
import socket from '../utils/socket';
import { deltaSyncMessages } from '../utils/messageSync';
import DraggableModal from '../components/DraggableModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExts = ['.mp4', '.mov', '.avi', '.webm', '.mkv'];
  const lower = url.split('?')[0].toLowerCase();
  return videoExts.some(ext => lower.endsWith(ext));
};

const buildMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  if (url.startsWith('/uploads/')) return `${BACKEND_URL}/api${url}`;
  return `${API}${url}`;
};

// Point 1 — persistent offline-send queue, tightly scoped to the Support
// chat type only (see point 1's own caution about an earlier broad-retry
// rollback). Stored per room so it survives a full page reload/app close
// while the customer is offline, and never mixes with other chat types.
const _supportQueueKey = (roomId) => `dws_support_send_queue_${roomId}`;
const _loadSupportQueue = (roomId) => {
  try {
    const arr = JSON.parse(localStorage.getItem(_supportQueueKey(roomId)) || '[]');
    return Array.isArray(arr) ? arr : [];
  } catch (_) { return []; }
};
const _saveSupportQueue = (roomId, queue) => {
  try { localStorage.setItem(_supportQueueKey(roomId), JSON.stringify(queue)); } catch (_) {}
};
const _removeFromSupportQueue = (roomId, clientMessageId) => {
  _saveSupportQueue(roomId, _loadSupportQueue(roomId).filter(m => m.clientMessageId !== clientMessageId));
};

// Props:
// chatType - override chat type (for embedded mode)
// isEmbedded - when true, adjusts styling for split screen
// onBack - callback for back button in embedded mode
const CustomerChat = ({ chatType: propChatType, isEmbedded = false, onBack }) => {
  const [searchParams] = useSearchParams();
  const chatType = propChatType || searchParams.get('type') || 'deposit';
  const navigate = useNavigate();
  
  const { user: authUser, logout, refreshUser } = useAuth();
  
  // Check for inquiry guest session
  const [guestUser, setGuestUser] = useState(null);
  const user = guestUser || authUser;  // Use guest user if present, otherwise auth user

  // Item 2 — support-restricted (device recovery) users: any device/browser
  // BACK gesture logs them out straight to the sign-in page (no settings/
  // profile/hub access). Re-doing "Trouble Sign In" returns them here.
  useEffect(() => {
    if (!authUser?.support_restricted) return;
    window.history.pushState(null, '', window.location.href);
    const onPop = () => { logout(true); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [authUser?.support_restricted, logout]);

  // P2 — while the account still carries guest/inquiry/restricted flags, re-check
  // the backend on focus so Deposit/Withdraw buttons appear the instant the
  // customer registers / is assigned an MB (no full re-login needed).
  useEffect(() => {
    if (!authUser?.id) return;
    if (!(authUser.is_guest || authUser.is_inquiry || authUser.support_restricted)) return;
    const onFocus = () => { if (refreshUser) refreshUser(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    onFocus();
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id, authUser?.is_guest, authUser?.is_inquiry, authUser?.support_restricted]);

  // FIX (June-2026): once an inquiry customer is registered (is_inquiry flips
  // false), move them off the stale ?type=inquiry screen onto the normal
  // multi-tab dashboard (/customer-select). Otherwise they stay stuck on the
  // inquiry chat where the Deposit/Withdraw/Support header CTA never renders,
  // so they never look like a normal customer even though the backend has
  // promoted them.
  useEffect(() => {
    if (isEmbedded) return;
    if (chatType !== 'inquiry') return;
    if (!authUser?.id) return;
    if (authUser.role === 'customer' && !authUser.is_inquiry && !authUser.is_guest) {
      navigate('/customer-select', { replace: true });
    }
  }, [isEmbedded, chatType, authUser?.id, authUser?.role, authUser?.is_inquiry, authUser?.is_guest, navigate]);

  // FIX (June-2026, root-cause): an inquiry GUEST session stored in
  // localStorage hardcodes is_guest/is_inquiry = true. `user = guestUser ||
  // authUser`, so even after the customer is registered + MB-assigned on the
  // backend, the device kept treating them as an inquiry guest → the
  // Deposit/Withdraw "Create Request" header CTA never rendered and the header
  // stayed on the inquiry branding ("DWS Team"). The existing refresh only
  // re-synced authUser, never the guest. Here we re-sync the guest's REAL
  // flags from the backend on mount + focus so the CTA/header update in place,
  // with no re-login (matching the app's "buttons appear the instant they're
  // promoted" design).
  useEffect(() => {
    const gid = guestUser?.id;
    if (!gid) return;                                   // pending session → no id yet
    if (!(guestUser.is_guest || guestUser.is_inquiry)) return; // already promoted
    let cancelled = false;
    const syncGuest = async () => {
      try {
        const res = await axios.get(`${API}/customers/profile/${gid}`);
        const c = res.data?.customer;
        if (!c || cancelled) return;
        const nextGuest = c.is_guest ?? false;
        const nextInquiry = c.is_inquiry ?? false;
        if (nextGuest === !!guestUser.is_guest && nextInquiry === !!guestUser.is_inquiry) return;
        setGuestUser((g) => (g ? {
          ...g,
          is_guest: nextGuest,
          is_inquiry: nextInquiry,
          support_restricted: c.support_restricted ?? false,
          mb_setup: c.mb_setup ?? g.mb_setup,
          role: c.role || g.role,
        } : g));
        // Persist so a remount reads the promoted flags (see mount effect).
        try {
          const sess = JSON.parse(localStorage.getItem('inquiry_session') || '{}');
          sess.is_guest = nextGuest;
          sess.is_inquiry = nextInquiry;
          localStorage.setItem('inquiry_session', JSON.stringify(sess));
        } catch (_) {}
      } catch (_) { /* transient / still guest — keep current */ }
    };
    syncGuest();
    const onFocus = () => syncGuest();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestUser?.id, guestUser?.is_guest, guestUser?.is_inquiry]);

  
  const { showNotification, playNotificationSound, requestPermission, updateAppBadge } = useWebNotification();
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;
  const [roomId, setRoomId] = useState(null);
  const [messages, setMessages] = useState([]);
  // Keep a live ref of messages so reconnect delta-sync can read the current
  // thread without stale closures.
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  const [loading, setLoading] = useState(false); // Changed to false for instant loading
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [inputKey, setInputKey] = useState(0);
  const [longPressMenu, setLongPressMenu] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const [show3DotMenu, setShow3DotMenu] = useState(false);
  // Session-40: Service Request creation modal state
  const [showSRModal, setShowSRModal] = useState(false);
  const [srAmount, setSrAmount] = useState('');
  const [srReference, setSrReference] = useState('');
  const [srBusy, setSrBusy] = useState(false);
  const [srPrimaryAccount, setSrPrimaryAccount] = useState(null);
  // Session-41: inline "new account" form fields for first-time withdraw
  const [srNewAcc, setSrNewAcc] = useState({ holder_name: '', account_number: '', ifsc: '', bank: '', upi_id: '' });
  // Session-42: locked-account "Change" warning modal
  const [showAccChangeWarn, setShowAccChangeWarn] = useState(false);
  // Session-48g: admin-configured quick chips (deposit + withdraw + support)
  const [srPresets, setSrPresets] = useState({
    deposit_enabled: true, withdraw_enabled: false, support_enabled: false,
    deposit_presets: [], withdraw_presets: [], support_presets: [],
  });
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [hoveredMsg, setHoveredMsg] = useState(null);
  const [pinnedMessages, setPinnedMessages] = useState([]); // point 43 — max 2
  const [pinDurationTarget, setPinDurationTarget] = useState(null);
  const [isChatMuted, setIsChatMuted] = useState(false); // point 52
  
  // Image Preview State
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [allMediaUrls, setAllMediaUrls] = useState([]);
  const [currentMediaIdx, setCurrentMediaIdx] = useState(0);
  
  // Support Menu
  const [showSupportMenu, setShowSupportMenu] = useState(false);
  // Client history (withdraw / deposit last-5 modal)
  const [historyModal, setHistoryModal] = useState(null); // 'withdraw' | 'deposit' | null
  const [supportInfo, setSupportInfo] = useState({ phone: null, whatsapp: null, email: null, support_link: null });
  
  // Display Settings (fetched from Admin settings)
  const [displaySettings, setDisplaySettings] = useState({
    inquiry_name: 'DWS Team',
    support_name: 'DWS Support',
    deposit_name: 'DWS Deposit',
    withdrawal_name: 'DWS Withdrawal',
    inquiry_dp: null,
    support_dp: null,
    deposit_dp: null,
    withdrawal_dp: null
  });
  
  // Status Viewer
  const [showStatusViewer, setShowStatusViewer] = useState(false);
  
  // Reply and Forward features
  const [replyingTo, setReplyingTo] = useState(null);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [availableChats, setAvailableChats] = useState([]);
  const [messageToForward, setMessageToForward] = useState(null);

  // Having-Trouble banner state (deposit/withdrawal only)
  const [showTroubleBanner, setShowTroubleBanner] = useState(false);
  const [showSoftNudge, setShowSoftNudge] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [botTyping, setBotTyping] = useState(false); // shown briefly after option click for perceived speed

  // Point 50 — the keyboard must only open when the user explicitly taps
  // the message box, never automatically on opening a chat. If a search
  // box (or any other input) was focused on the previous screen, the
  // mobile keyboard can otherwise carry over into this page on mount.
  useEffect(() => {
    const active = document.activeElement;
    if (active && active !== document.body && typeof active.blur === 'function') {
      active.blur();
    }
  }, []);

  // Point 52 — mute-chat status for this room.
  useEffect(() => {
    if (!roomId || !user?.id) return;
    let cancelled = false;
    axios.get(`${API}/chat/room/${roomId}/mute-status`, { params: { user_id: user.id } })
      .then(r => { if (!cancelled) setIsChatMuted(!!r.data?.is_muted); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [roomId, user?.id]);

  // Session-49: admin-configured Chat Header Labels for the CUSTOMER role.
  // Also live-refreshes via socket when admin edits & saves in Settings.
  // Point 4 — pure display-only text, so it paints instantly from cache
  // (fixes the "delay opening a chat/header" symptom) then refreshes
  // silently once the live labels come back.
  const [hdrLabels, setHdrLabels] = useState({});
  useEffect(() => {
    const t = (chatType === 'withdrawal' ? 'withdraw' : chatType);
    if (!t) return;
    const cached = readInstantCache('header_labels', `${t}_customer`);
    if (cached) setHdrLabels(cached);
    let cancelled = false;
    axios.get(`${API}/header-labels?type=${t}&role=customer`)
      .then(r => {
        if (cancelled) return;
        setHdrLabels(r.data || {});
        writeInstantCache('header_labels', `${t}_customer`, r.data || {});
      })
      .catch(() => {});
    const listener = (payload) => {
      if (!payload || payload.chat_type !== t || payload.role !== 'customer') return;
      setHdrLabels(prev => ({ ...prev, ...(payload.labels || {}) }));
    };
    onHeaderLabelsUpdated(listener);
    return () => { cancelled = true; offHeaderLabelsUpdated(listener); };
  }, [chatType]);

  // Session-49: "Having Trouble?" + soft-nudge banners REMOVED per user
  // request. They used to auto-inject "Move to Support" prompts on
  // deposit/withdraw chats after 2 / 5 min of no reply, but were confusing
  // customers. Kept state variables (permanently false) so the older render
  // block below stays a no-op without touching unrelated JSX.

  // Auto-resolution check: when opening a support chat, ask the backend to inject
  // "क्या समस्या हल हुई?" if handler replied and it's been quiet for 5+ min
  useEffect(() => {
    if (chatType !== 'support' || !user?.id) return;
    const t = setTimeout(() => {
      axios.post(`${API}/support/auto-resolution-check`, {
        customer_id: user.id,
        stale_minutes: 5,
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [chatType, user?.id]);

  // Fallback polling for support chat. Bot AND handler messages already arrive
  // instantly over the socket (_emit_bot_message emits `new_message` to the
  // room + user rooms), so this is only a safety-net for a degraded/polling
  // transport. Slowed 4s -> 15s to cut full-message-reload load ~4x under 200-
  // 300 concurrent customers, and gated to the foreground tab only.
  useEffect(() => {
    if (chatType !== 'support' || !roomId) return;
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadMessagesRef.current(roomId);
      }
    }, 15000);
    return () => clearInterval(poll);
  }, [chatType, roomId]);

  // Reset pagination state when switching rooms
  useEffect(() => {
    setHasMoreOlder(true);
    setOlderLoading(false);
  }, [roomId]);

  const handleEscalateToSupport = async () => {
    if (!user?.id) return;
    setEscalating(true);
    try {
      const res = await axios.post(`${API}/support/escalate`, {
        customer_id: user.id,
        from_chat_type: chatType,
      });
      // Navigate to support chat (use correct customer-chat route)
      navigate(`/customer-chat?type=support`);
    } catch (e) {
      console.error('Escalate failed', e);
      alert('Escalation failed, please try again');
    } finally {
      setEscalating(false);
    }
  };

  // Quick Reply Suggestions (shown above keyboard)
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  
  // Phase 2: Advanced features
  const [handlerTyping, setHandlerTyping] = useState(false);
  const [handlerOnlineStatus, setHandlerOnlineStatus] = useState({ is_online: false, last_seen: null });
  // Offline-cache indicator — true when the browser reports a live network.
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  useEffect(() => {
    idbCleanupOld();
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  
  // Track which messages have already played notification sound
  const notifiedMessageIds = useRef(new Set());
  // Track recently added message IDs to prevent brief duplicates
  const recentlyAddedMsgIds = useRef(new Set());
  const [handlerId, setHandlerId] = useState(null);
  
  const messagesEndRef = useRef(null);
  const roomIdRef = useRef(null);
  const typingCheckRef = useRef(null);
  const inputRef = useRef(null);
  const formRef = useRef(null);
  const chatContainerRef = useRef(null);
  const containerRef = useRef(null);
  // Point-1 — measure the live chat-header height so the message list offsets
  // by the header's ACTUAL height (WhatsApp-fixed; no overlap when it grows).
  const chatHeaderRef = useRef(null);
  const headerHeight = useElementHeight(chatHeaderRef, 56);
  // Pagination state — infinite scroll for messages (WhatsApp-style)
  const [olderLoading, setOlderLoading] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  
  // Visual Viewport Keyboard Fix for PWA (Android Chrome + iOS Safari)
  // Pin BOTH height and top of the container to the visual viewport so the
  // input bar stays flush above the keyboard. On keyboard-open (viewport
  // shrinks by >100px) also auto-scroll to the newest message — WhatsApp
  // style — so the last bubble never hides behind the composer/keyboard.
  useEffect(() => {
    const el = containerRef.current;
    const vv = window.visualViewport;
    if (!el || !vv) return;
    let lastHeight = vv.height;
    const apply = () => {
      el.style.height = `${vv.height}px`;
      el.style.top = `${vv.offsetTop}px`;
      if (vv.height < lastHeight - 100) {
        requestAnimationFrame(() => {
          try {
            const c = chatContainerRef.current;
            if (c) c.scrollTop = c.scrollHeight;
            if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ block: 'end' });
          } catch {}
        });
      }
      lastHeight = vv.height;
    };
    apply();
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    return () => {
      vv.removeEventListener('resize', apply);
      vv.removeEventListener('scroll', apply);
      if (el) { el.style.height = ''; el.style.top = ''; }
    };
  }, []);
  const inputContainerRef = useRef(null);
  const longPressTimer = useRef(null);
  const menuRef = useRef(null);

  // Listen for incoming calls (for receiving conference invites)
  useEffect(() => {
    const handleIncomingCall = (data) => {
      if (data.caller_id !== user?.id) {
        setIncomingCall({
          callId: data.call_id,
          callerId: data.caller_id,
          callerName: data.caller_name,
          callerProfilePic: data.caller_profile_pic,
          isConference: data.is_conference || false,
          participantNames: data.participant_names || []
        });
      }
    };
    socket.on('incoming_call', handleIncomingCall);
    return () => socket.off('incoming_call', handleIncomingCall);
  }, [user]);

  const EMOJIS = ['❤️', '👍', '😂', '😮', '😢', '🙏'];

  // Date separator helpers
  const getDateLabel = (timestamp) => {
    const msgDate = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (msgDate.toDateString() === today.toDateString()) return 'Today';
    if (msgDate.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return msgDate.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const shouldShowDateSeparator = (msgs, idx) => {
    if (idx === 0) return true;
    return new Date(msgs[idx - 1].timestamp).toDateString() !== new Date(msgs[idx].timestamp).toDateString();
  };

  // Session-53 hardening — treat data-URI SVGs as stickers too.
  const isStickerLike = (m) => !!(m && (m.is_sticker || (typeof m.image_url === 'string' && m.image_url.startsWith('data:image/svg'))));

  const getImageGroup = (msgs, idx) => {
    const msg = msgs[idx];
    // Bug #1 fix (Session-53) — added is_sticker guards so stickers are
    // never merged with photos in a collage. Was rendering sticker inside
    // photo group AND standalone (double-render "error").
    if (!msg.image_url || msg.voice_url || msg.document_url || isVideoUrl(msg.image_url) || isStickerLike(msg)) return null;
    // First photo of group must NOT have caption
    if (msg.caption || (msg.text && msg.text.trim() && msg.text !== '🖼️ Photo' && msg.text !== '🎥 Video')) return null;
    if (idx > 0 && msgs[idx - 1].image_url && !msgs[idx - 1].voice_url && !msgs[idx - 1].document_url && !isVideoUrl(msgs[idx - 1].image_url) && !isStickerLike(msgs[idx - 1]) && msgs[idx - 1].sender_id === msg.sender_id && !msgs[idx - 1].caption && (!msgs[idx - 1].text || !msgs[idx - 1].text.trim() || msgs[idx - 1].text === '🖼️ Photo' || msgs[idx - 1].text === '🎥 Video')) return null;
    const group = [msg];
    for (let i = idx + 1; i < msgs.length; i++) {
      if (group.length >= 10) break;
      const nextMsg = msgs[i];
      if (!nextMsg.image_url || nextMsg.voice_url || nextMsg.document_url || isVideoUrl(nextMsg.image_url) || isStickerLike(nextMsg) || nextMsg.sender_id !== msg.sender_id) break;
      const hasCaption = nextMsg.caption || (nextMsg.text && nextMsg.text.trim() && nextMsg.text !== '🖼️ Photo' && nextMsg.text !== '🎥 Video');
      if (!hasCaption) {
        group.push(nextMsg);
      } else {
        // Point 6 — a captioned photo is standalone; stop WITHOUT adding it.
        break;
      }
    }
    return group.length > 1 ? group : null;
  };

  const isPartOfImageGroup = (msgs, idx) => {
    const msg = msgs[idx];
    if (!msg.image_url || msg.voice_url || msg.document_url || isVideoUrl(msg.image_url) || isStickerLike(msg) || idx === 0) return false;
    // Point 6 — a captioned photo is always its own standalone message.
    if (msg.caption || (msg.text && msg.text.trim() && msg.text !== '🖼️ Photo' && msg.text !== '🎥 Video')) return false;
    let groupCount = 0;
    for (let i = idx - 1; i >= Math.max(0, idx - 10); i--) {
      const prevMsg = msgs[i];
      if (prevMsg.image_url && !prevMsg.voice_url && !prevMsg.document_url && !isVideoUrl(prevMsg.image_url) && !isStickerLike(prevMsg) && prevMsg.sender_id === msg.sender_id && !prevMsg.caption && (!prevMsg.text || !prevMsg.text.trim() || prevMsg.text === '🖼️ Photo' || prevMsg.text === '🎥 Video')) {
        groupCount++;
      } else break;
    }
    return groupCount > 0 && groupCount < 10;
  };

  // Ref to track previous messages count for notification logic
  const prevMessagesCountRef = useRef(0);
  // Flag to skip notification on initial load — stays true for 3 seconds after mount
  const isInitialLoadRef = useRef(true);
  useEffect(() => {
    isInitialLoadRef.current = true;
    const timer = setTimeout(() => { isInitialLoadRef.current = false; }, 3000);
    return () => clearTimeout(timer);
  }, []);
  
  // Stable loadMessages function - no dependencies that change frequently
  // Fix doc #1 — keep cached history fresh as messages change (incl. socket updates).
  useEffect(() => {
    if (roomId && Array.isArray(messages) && messages.length) {
      writeInstantCache('room_messages', roomId, messages.slice(-50));
    }
  }, [messages, roomId]);

  const loadMessages = useCallback(async (room) => {
    if (!room) {
      console.log('⚠️ Cannot load messages: No room');
      return;
    }
    
    try {
      // Fix doc #1 — instant paint from cached history on first open, then refresh below.
      const _cachedMsgs = readInstantCache('room_messages', room);
      if (_cachedMsgs && Array.isArray(_cachedMsgs)) {
        setMessages(prev => (prev && prev.length) ? prev : _cachedMsgs);
      }
      // Durable IndexedDB cache — instant paint even offline / after full app close.
      idbGetMessages(room).then(idbMsgs => {
        if (idbMsgs && idbMsgs.length) setMessages(prev => (prev && prev.length) ? prev : idbMsgs);
      }).catch(() => {});
      // Pagination: load latest 50 messages initially (older loaded on scroll-up)
      const response = await axios.get(`${API}/chat/messages/${room}`, {
        params: { user_id: user?.id, limit: 50 }
      });
      
      if (response.data && Array.isArray(response.data)) {
        idbSaveMessages(room, response.data);
        console.log(`✅ Loaded ${response.data.length} messages for room ${room}`);
        // If fewer than page size returned, no more older to fetch
        setHasMoreOlder((response.data.length || 0) >= 50);
        
        // Skip notifications during initial load period (first 3 seconds)
        if (isInitialLoadRef.current) {
          prevMessagesCountRef.current = response.data.length;
          response.data.forEach(msg => notifiedMessageIds.current.add(msg.id));
          setMessages(response.data);
          return;
        }
        
        // Check for new messages from admin/handler and show notification
        const prevMessagesLength = prevMessagesCountRef.current;
        const newMessages = response.data;
        
        if (prevMessagesLength > 0 && newMessages.length > prevMessagesLength) {
          // Get only the NEW messages that weren't there before
          const newlyAddedMessages = newMessages.slice(prevMessagesLength);
          
          // Process each new message
          newlyAddedMessages.forEach((newMsg) => {
            // Only notify if:
            // 1. Message is from admin/handler (not from customer)
            // 2. We haven't already notified for this message
            if (newMsg.sender_id !== user?.id && 
                newMsg.sender_role !== 'customer' &&
                !notifiedMessageIds.current.has(newMsg.id)) {
              
              console.log('🔔 New message from admin/handler, showing notification for message:', newMsg.id);
              
              // Mark this message as notified
              notifiedMessageIds.current.add(newMsg.id);
              
              const senderName = newMsg.sender_role === 'admin' ? 'Admin' : 'Handler';
              const messagePreview = newMsg.message_type === 'text' 
                ? newMsg.message_text 
                : newMsg.message_type === 'image' 
                ? '📷 Photo' 
                : newMsg.message_type === 'video'
                ? '🎥 Video'
                : newMsg.message_type === 'voice'
                ? '🎤 Voice message'
                : 'New message';
              
              showNotificationRef.current({
                title: `${senderName} - DWS Vault Chat`,
                body: messagePreview,
                tag: `chat-${room}`,
                data: {
                  roomId: room,
                  senderId: newMsg.sender_id,
                  messageType: newMsg.message_type,
                  chatType: chatType
                }
              });
            }
          });
        }
        
        // Update ref for next comparison
        prevMessagesCountRef.current = newMessages.length;
        setMessages(response.data);
      } else {
        console.warn('⚠️ Invalid response:', response.data);
        // Don't clear messages on invalid response
      }
    } catch (error) {
      console.error('❌ Load messages failed:', error);
      // Don't clear existing messages on error
    }
  }, [user?.id, chatType]); // showNotification via ref

  // Load OLDER messages when user scrolls to the top (WhatsApp-style pagination)
  const loadOlderMessages = useCallback(async () => {
    const room = roomIdRef.current;
    if (!room || !user?.id) return;
    if (olderLoading || !hasMoreOlder) return;
    if (!messages || messages.length === 0) return;
    const oldest = messages[0]?.timestamp;
    if (!oldest) return;

    setOlderLoading(true);
    const container = chatContainerRef.current;
    const prevHeight = container?.scrollHeight || 0;
    const prevTop = container?.scrollTop || 0;

    try {
      const resp = await axios.get(`${API}/chat/messages/${room}`, {
        params: { user_id: user.id, before: oldest, limit: 50 }
      });
      if (resp.data && Array.isArray(resp.data)) {
        const older = resp.data;
        if (older.length === 0) {
          setHasMoreOlder(false);
        } else {
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newOlder = older.filter(m => !existingIds.has(m.id));
            newOlder.forEach(m => notifiedMessageIds.current.add(m.id));
            return [...newOlder, ...prev];
          });
          setHasMoreOlder(older.length >= 50);
          requestAnimationFrame(() => {
            if (container) {
              const newHeight = container.scrollHeight;
              container.scrollTop = prevTop + (newHeight - prevHeight);
            }
          });
        }
      }
    } catch (e) {
      console.error('Failed to load older messages:', e);
    } finally {
      setOlderLoading(false);
    }
  }, [user?.id, olderLoading, hasMoreOlder, messages]);

  // Handle image preview
  const [allMediaCaptions, setAllMediaCaptions] = useState([]);
  
  const handleImageClick = (imageUrl) => {
    const mediaMessages = messages.filter(m => m.image_url);
    const mediaList = mediaMessages.map(m => {
      if (m.image_url.startsWith('http')) return m.image_url;
      if (m.image_url.startsWith('/api/')) return `${BACKEND_URL}${m.image_url}`;
      if (m.image_url.startsWith('/uploads/')) return `${BACKEND_URL}/api${m.image_url}`;
      return `${API}${m.image_url}`;
    });
    const captionList = mediaMessages.map(m => m.caption || m.text || '');
    setAllMediaUrls(mediaList);
    setAllMediaCaptions(captionList);
    const idx = mediaList.indexOf(imageUrl);
    setCurrentMediaIdx(idx >= 0 ? idx : 0);
    setPreviewImageUrl(imageUrl);
    setShowImagePreview(true);
  };

  const closeImagePreview = () => {
    setShowImagePreview(false);
    setPreviewImageUrl(null);
  };

  // Handle input focus - Gentle keyboard fix (avoid too many scrolls)
  const handleInputFocus = () => {
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }, 300);
  };

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  // Scroll to bottom on initial load and when new messages arrive
  const prevMsgLenRef = useRef(0);
  useEffect(() => {
    if (messages.length > 0) {
      if (prevMsgLenRef.current === 0) {
        // Initial load - scroll after DOM paints
        scrollToBottom();
        requestAnimationFrame(scrollToBottom);
        setTimeout(scrollToBottom, 100);
        setTimeout(scrollToBottom, 500);
      } else if (messages.length > prevMsgLenRef.current && !isUserScrolling) {
        // New message arrived - scroll in next paint frame (prevents bounce)
        requestAnimationFrame(scrollToBottom);
      }
      prevMsgLenRef.current = messages.length;
    }
  }, [messages.length, roomId]);

  // Detect if user is scrolling up to read old messages + trigger load older near top
  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    // If user is more than 100px from bottom, they're reading old messages
    setIsUserScrolling(distanceFromBottom > 100);
    
    // Near the top? Load older messages (WhatsApp-style infinite scroll)
    if (scrollTop < 120 && hasMoreOlder && !olderLoading) {
      loadOlderMessages();
    }
  };

  // Visual Viewport API - Detect keyboard open/close (disabled to prevent jumps)
  useEffect(() => {
    // Disabled: This was causing screen jumps
    // Keep empty to prevent jumps while maintaining component structure
  }, []);

  // Store loadMessages in a ref to avoid dependency issues
  const loadMessagesRef = useRef(loadMessages);
  useEffect(() => { loadMessagesRef.current = loadMessages; }, [loadMessages]);
  useEffect(() => {
    const onReconnect = async () => {
      const rid = roomIdRef.current;
      if (!rid) return;
      try {
        const did = await deltaSyncMessages({
          roomId: rid, userId: user?.id,
          getMessages: () => messagesRef.current,
          setMessages,
        });
        if (!did) loadMessagesRef.current(rid);
      } catch (_) {
        try { loadMessagesRef.current(rid); } catch (_) {}
      }
    };
    window.addEventListener('socket:reconnected', onReconnect);
    return () => window.removeEventListener('socket:reconnected', onReconnect);
  }, []);

  // Fetch display settings from Admin
  useEffect(() => {
    const fetchDisplaySettings = async () => {
      try {
        const response = await axios.get(`${API}/settings/display-names`);
        if (response.data) {
          setDisplaySettings(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        console.log('Using default display names');
      }
    };
    fetchDisplaySettings();
  }, []);

  // Get display name based on chat type
  const getDisplayName = () => {
    if (user?.is_inquiry) return displaySettings.inquiry_name || 'DWS Team';
    switch (chatType) {
      case 'support': return displaySettings.support_name || 'DWS Support';
      case 'deposit': return displaySettings.deposit_name || 'DWS Deposit';
      case 'withdrawal': return displaySettings.withdrawal_name || 'DWS Withdrawal';
      default: return displaySettings.deposit_name || 'DWS Team';
    }
  };

  // Get display DP based on chat type
  const getDisplayDP = () => {
    if (user?.is_inquiry) return displaySettings.inquiry_dp;
    switch (chatType) {
      case 'support': return displaySettings.support_dp;
      case 'deposit': return displaySettings.deposit_dp;
      case 'withdrawal': return displaySettings.withdrawal_dp;
      default: return displaySettings.deposit_dp;
    }
  };

  // Check for inquiry guest session on mount
  useEffect(() => {
    const inquirySession = localStorage.getItem('inquiry_session');
    if (inquirySession) {
      try {
        const session = JSON.parse(inquirySession);
        
        // Check if this is a pending session (needs to be created on first message)
        if (session.is_pending) {
          // Set a temporary guest user for UI display
          setGuestUser({
            id: null,  // No ID yet - will be created on first message
            username: 'New Inquiry',
            phone: 'PENDING',
            role: 'customer',
            is_guest: true,
            is_inquiry: true,
            is_pending: true,  // Flag to create session on first message
            ip_address: session.ip_address,
            referrer: session.referrer
          });
          console.log('👤 Pending inquiry guest - session will be created on first message');
        } else {
          // Existing session - use stored data. Flags default to guest/inquiry
          // but are read back from the session so a backend-sync (below) that
          // promoted this customer survives a remount instead of snapping back
          // to the hardcoded guest state.
          setGuestUser({
            id: session.guest_user_id,
            username: session.guest_name,
            phone: `GUEST_${session.guest_name}`,
            role: 'customer',
            is_guest: session.is_guest ?? true,
            is_inquiry: session.is_inquiry ?? true
          });
          console.log('👤 Inquiry guest detected:', session.guest_name);
        }
      } catch (err) {
        console.error('Failed to parse inquiry session:', err);
      }
    } else if (!authUser) {
      // No inquiry session and no auth user - redirect to login
      navigate('/customer-login', { replace: true });
    }
  }, [authUser, navigate]);

  useEffect(() => {
    if (!user) return;

    // Initialize chat
    const initChat = async () => {
      try {
        console.log('🔄 Initializing chat for user:', user.id, 'type:', chatType);
        
        // For pending inquiry users (no ID yet), skip chat initialization
        // They will get their session when they send their first message
        if (user.is_pending) {
          console.log('ℹ️ Pending inquiry user - waiting for first message');
          setLoading(false);
          setRoomId(null);
          setMessages([]);
          // Show quick reply suggestions instead of auto-fill
          setShowQuickReplies(true);
          return;
        }
        
        // Parallel requests for faster loading
        const chatStartPromise = axios.post(`${API}/chat/start`, {
          customer_id: user.id,
          chat_type: chatType,
          ip_address: null
        });
        
        const response = await chatStartPromise;
        console.log('📦 Chat start response:', response.data);
        
        // Room might not exist yet (will be created on first message)
        if (response.data.exists && response.data.room_id) {
          const room = response.data.room_id;
          console.log('✅ Room found:', room);
          setRoomId(room);
          roomIdRef.current = room;
          
          // Set loading false immediately for instant UI
          setLoading(false);
          
          // Show quick reply suggestions every time chat opens
          setShowQuickReplies(true);

          // Load initial messages immediately
          loadMessagesRef.current(room);
          
          // Fetch pinned messages for this room (point 43 — up to 2)
          try {
            const roomData = await axios.get(`${API}/chat/rooms?user_id=${user.id}&role=customer`);
            const thisRoom = roomData.data.find(r => r.id === room);
            const pins = thisRoom?.pinned_messages || [];
            if (pins.length > 0) {
              const msgsRes = await axios.get(`${API}/chat/messages/${room}`);
              const pinIds = pins.map(p => p.message_id);
              const pinMsgs = pinIds.map(id => msgsRes.data.find(m => m.id === id)).filter(Boolean);
              setPinnedMessages(pinMsgs);
            } else {
              setPinnedMessages([]);
            }
          } catch(e) {}
          
          console.log('✅ Room ready, Socket.IO will handle real-time messages:', room);
          
        } else {
          // No room for this chat type yet - First time chat
          console.log('ℹ️ No existing room for', chatType, '- will create on first message');
          setRoomId(null);
          setMessages([]);
          setLoading(false);
          
          // Show quick reply suggestions for first time users
          setShowQuickReplies(true);
        }

      } catch (error) {
        console.error('❌ Failed to initialize chat:', error);
        setLoading(false);
        
        // Retry after 1 second if failed (faster retry) - but not for pending users
        if (!user.is_pending) {
          setTimeout(() => {
            console.log('🔄 Retrying chat initialization...');
            initChat();
          }, 1000);
        }
      }
    };

    initChat();

    // Handle page visibility change - instant refresh when tab returns
    const cleanupVisibility = onVisibilityFetch(() => {
      if (roomIdRef.current) {
        loadMessagesRef.current(roomIdRef.current);
      }
    });

    // Cleanup on unmount
    return () => {
      cleanupVisibility();
    };
  }, [user, chatType]); // Removed loadMessages from dependencies - using ref instead

  // Socket.IO real-time messaging (alongside polling for zero-risk)
  useEffect(() => {
    if (!roomId || !user?.id) return;
    
    connectSocket(user.id);
    joinRoom(roomId, user.id);
    console.log('🔌 Socket.IO joined room:', roomId);
    
    const handleNewMessage = (data) => {
      if (data.room_id === roomId && data.message) {
        const msgId = data.message.id;
        // Skip if recently added locally (prevents brief duplicate)
        if (recentlyAddedMsgIds.current.has(msgId)) return;
        notifiedMessageIds.current.add(msgId);
        setMessages(prev => {
          // Bug #3 fix (Session-53) — unified dedupe. Match on server id
          // OR client_message_id so a socket echo doesn't create a second
          // bubble when the axios response already inserted the message.
          const cmid = data.message.client_message_id;
          const existingIdx = prev.findIndex(m =>
            m.id === msgId || (cmid && m.client_message_id === cmid)
          );
          if (existingIdx !== -1) {
            const next = prev.slice();
            next[existingIdx] = { ...prev[existingIdx], ...data.message, pending: false, _uploading: false };
            return next;
          }
          return [...prev, data.message];
        });
        idbSaveMessages(roomId, [data.message]);
      }
    };
    
    // Listen for typing indicators via Socket.IO
    const handleUserTyping = (data) => {
      if (data.room_id === roomId && data.user_id !== user.id) {
        setHandlerTyping(data.is_typing);
      }
    };
    
    // Listen for chat move (handler moved customer to different panel)
    const handleChatMoved = (data) => {
      if (data.old_room_id === roomId) {
        toast.info(`Moved to ${data.new_type} panel`);
        // Navigate directly to the new chat type (not dashboard)
        navigate(`/customer-chat?type=${data.new_type}`, { replace: true });
      }
    };
    
    // Listen for read receipts (handler read our messages → update ticks)
    const handleMessagesRead = (data) => {
      if (data.room_id !== roomId) return;
      if (Array.isArray(data.message_ids) && data.message_ids.length) {
        const ids = new Set(data.message_ids);
        setMessages(prev => prev.map(m =>
          ids.has(m.id) ? { ...m, delivered: true, read: true } : m
        ));
      } else if (data.auto || data.read_all) {
        // Staff replied (auto-marks our prior messages read) or a bulk read —
        // no id list is sent, so turn ALL of our own outgoing messages blue.
        setMessages(prev => prev.map(m =>
          m.sender_id === user.id ? { ...m, delivered: true, read: true } : m
        ));
      }
    };

    // Listen for message updates (revert strike-through, edits)
    const handleMessageUpdated = (data) => {
      if (data.room_id === roomId && data.message_id) {
        setMessages(prev => prev.map(m =>
          m.id === data.message_id
            ? { ...m,
                text: data.text ?? m.text,
                meta: { ...(m.meta || {}), ...(data.meta || {}) },
                edited_at: data.edited_at || m.edited_at,
                text_striked: data.text_striked !== undefined ? data.text_striked : m.text_striked,
                revert_reason: data.revert_reason !== undefined ? data.revert_reason : m.revert_reason,
                reverted_at: data.reverted_at || m.reverted_at }
            : m
        ));
      }
    };
    
    // Listen for delivery receipts (message reached staff's device →
    // single grey tick becomes DOUBLE grey). Without this the customer never
    // saw "delivered" and the tick jumped straight to blue only on open.
    const handleMessagesDelivered = (data) => {
      if (data.room_id !== roomId || !Array.isArray(data.message_ids)) return;
      const ids = new Set(data.message_ids);
      setMessages(prev => prev.map(m =>
        ids.has(m.id) ? { ...m, delivered: true } : m
      ));
    };

    onNewMessage(handleNewMessage);
    onUserTyping(handleUserTyping);
    onMessagesRead(handleMessagesRead);
    onMessagesDelivered(handleMessagesDelivered);
    onMessageUpdated(handleMessageUpdated);
    const handleMessageDeleted = ({ message_id, delete_for_everyone }) => {
      if (!delete_for_everyone) return;
      setMessages(prev => prev.filter(m => m.id !== message_id));
      idbDeleteMessage(message_id);
    };
    onMessageDeleted(handleMessageDeleted);
    socket.on('chat_moved', handleChatMoved);
    
    return () => {
      offNewMessage(handleNewMessage);
      offUserTyping(handleUserTyping);
      offMessagesRead(handleMessagesRead);
      offMessagesDelivered(handleMessagesDelivered);
      offMessageUpdated(handleMessageUpdated);
      offMessageDeleted(handleMessageDeleted);
      socket.off('chat_moved', handleChatMoved);
      leaveRoom(roomId, user.id);
    };
  }, [roomId, user?.id]);

  // Phase 2: Update online status.
  // Socket presence owns the offline flip (15s grace on disconnect) — no
  // is_online=false on unmount to prevent flicker.
  useEffect(() => {
    if (user?.id) {
      console.log('[CustomerChat] Setting user online:', user.id);
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: true
      })
      .then(() => console.log('[CustomerChat] ✅ Online status updated'))
      .catch(err => console.error('[CustomerChat] ❌ Online status update failed:', err));
    }
  }, [user]);

  // Phase 2: Mark messages as read (only when user is actively viewing chat)
  useEffect(() => {
    if (roomId && messages.length > 0 && user?.id && document.visibilityState === 'visible') {
      const unreadIds = messages
        .filter(m => !m.read && m.sender_id !== user.id)
        .map(m => m.id);
      
      if (unreadIds.length > 0) {
        // Mark as read immediately when viewing chat
        console.log('[CustomerChat] Marking messages as read:', unreadIds.length);
        markMessagesRead(roomId, unreadIds, user.id);
        axios.post(`${API}/messages/mark-read?user_id=${user.id}`, {
          message_ids: unreadIds
        })
        .then(() => {
          console.log('[CustomerChat] ✅ Messages marked as read');
          // Issue 16 — reading the chat clears the device PWA badge.
          try { updateAppBadge(0); } catch (_) {}
        })
        .catch(err => console.error('[CustomerChat] ❌ Mark read failed:', err));
      }
    }
  }, [messages, roomId, user]);

  // Phase 2: Typing indicator is now 100% socket-driven via the `user_typing`
  // event (see handleUserTyping / onUserTyping above). The old 2s HTTP poll of
  // GET /chat/typing was removed — at 200-300 concurrent customers it generated
  // ~150 req/s of pure overhead for zero benefit (socket is real-time + lighter).

  // Fetch support info - LOAD IMMEDIATELY ON MOUNT
  useEffect(() => {
    const fetchSupportInfo = async () => {
      try {
        console.log('📞 Fetching support info...');
        // Add timestamp to prevent caching
        const response = await axios.get(`${API}/settings/support-info?t=${Date.now()}`);
        console.log('✅ Support info loaded:', response.data);
        setSupportInfo(response.data);
      } catch (err) {
        console.error('❌ Failed to fetch support info:', err);
        // Set empty defaults on error
        setSupportInfo({ phone: null, whatsapp: null, email: null, support_link: null });
      }
    };
    
    fetchSupportInfo();
  }, []); // Load once on mount, never again

  // Phase 2: Fetch handler info (once) and online status (polling)
  useEffect(() => {
    let handlerFound = false;
    
    const fetchHandlerInfo = async () => {
      if (!roomId || handlerFound) return;
      try {
        const roomRes = await axios.get(`${API}/chat/room/${roomId}`);
        const room = roomRes.data;
        const actualHandlerId = room.assigned_handler_id || room.handler_id;
        if (actualHandlerId) {
          setHandlerId(actualHandlerId);
          handlerFound = true;
        }
      } catch (err) {
        console.error('Fetch handler info failed:', err);
      }
    };

    const fetchOnlineStatus = async () => {
      try {
        const globalRes = await axios.get(`${API}/global-online-status`);
        setHandlerOnlineStatus(globalRes.data);
      } catch (err) {
        console.error('Fetch online status failed:', err);
      }
    };

    // Fetch handler info once
    fetchHandlerInfo();
    // Fetch online status immediately
    fetchOnlineStatus();

    // Slow fallback polling — socket-driven presence broadcasts user_online /
    // user_offline on every handler connect/disconnect so /global-online-status
    // stays fresh. This 15s poll is just a safety net.
    const interval = setInterval(() => {
      if (!handlerFound) fetchHandlerInfo();
      fetchOnlineStatus();
    }, 15000);

    return () => clearInterval(interval);
  }, [roomId]);

  // Heartbeat: Update own online status.
  // Socket-driven presence owns online/offline (register_user → online,
  // disconnect + 15s grace → offline). This HTTP heartbeat is now a slow
  // 30s safety net and no longer flips offline on unmount to avoid flicker.
  useEffect(() => {
    if (!user?.id) return;

    axios.post(`${API}/users/online-status?user_id=${user.id}`, {
      is_online: true
    }).catch(err => console.error('Online status update failed:', err));

    const heartbeat = setInterval(() => {
      axios.post(`${API}/users/online-status?user_id=${user.id}`, {
        is_online: true
      }).catch(err => console.error('Heartbeat failed:', err));
    }, 30000);

    return () => {
      clearInterval(heartbeat);
    };
  }, [user]);

  // Session-40: submit a Deposit / Withdraw / Support service request.
  // Session-48: fetch admin-configured quick-amount chip presets on mount
  // + subscribe to live updates via socket so admin edits reflect instantly.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/settings/custom-amount-presets`)
      .then(r => { if (alive) setSrPresets(r.data || {}); })
      .catch(() => {});
    const handler = (payload) => {
      if (!alive) return;
      const p = payload?.presets || payload;
      if (p) setSrPresets({
        deposit_enabled: !!p.deposit_enabled,
        withdraw_enabled: !!p.withdraw_enabled,
        support_enabled: !!p.support_enabled,
        deposit_presets: p.deposit_presets || [],
        withdraw_presets: p.withdraw_presets || [],
        support_presets: p.support_presets || [],
      });
    };
    if (typeof window !== 'undefined' && window.socket) {
      window.socket.on('custom_amount_presets_updated', handler);
    }
    return () => {
      alive = false;
      if (typeof window !== 'undefined' && window.socket) {
        try { window.socket.off('custom_amount_presets_updated', handler); } catch {}
      }
    };
  }, []);

  // Session-48g helper: ensure a chat room exists BEFORE creating a request.
  // Bug fix — a brand-new customer used to be blocked from raising a request
  // until they typed a first message (roomId was null). Now we spin up the
  // room on demand right here.
  const ensureRoomForRequest = async () => {
    if (roomId) return roomId;
    if (!user?.id) return null;
    try {
      const backendType = chatType === 'withdrawal' ? 'withdrawal' : chatType;
      const res = await axios.post(`${API}/chat/create-room`, {
        customer_id: user.id,
        chat_type: backendType,
      });
      const rid = res.data?.room_id || res.data?.id;
      if (rid) setRoomId(rid);
      return rid;
    } catch (e) {
      toast.error('Could not open chat — try again');
      return null;
    }
  };

  // Session-48: quick-chip click → instant send with `label` (no manual submit).
  const submitSRQuickChip = async (preset) => {
    if (!user?.id || srBusy) return;
    const rid = await ensureRoomForRequest();
    if (!rid) return;
    setSrBusy(true);
    try {
      const backendType = chatType === 'withdrawal' ? 'withdraw' : chatType;
      const payload = {
        type: backendType,
        room_id: rid,
        actor_id: user.id,
        label: preset.label,
      };
      // Support chips are text-only → also stuff the label into reference
      // so the backend (which requires reference for support type) accepts.
      if (backendType === 'support') {
        payload.reference = preset.label;
      } else if (preset.kind !== 'text' && preset.amount != null) {
        payload.amount = Number(preset.amount);
      }
      await axios.post(`${API}/service-requests/create`, payload);
      setShowSRModal(false);
      setSrAmount(''); setSrReference('');
      toast.success(`✅ ${preset.label} — form sent`);
      // Fix doc #7 — instant display of the created card (+ delayed safety net)
      const _prid = roomIdRef.current || payload.room_id;
      setTimeout(() => loadMessagesRef.current(_prid), 300);
      setTimeout(() => loadMessagesRef.current(_prid), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send');
    } finally { setSrBusy(false); }
  };


  const submitSRCreate = async () => {
    if (!user?.id || srBusy) return;
    if (chatType === 'support' && !srReference.trim()) return;
    // Session-48b: Deposit + Withdraw are amount-only. Reference field removed.
    if (chatType !== 'support' && !srAmount) return;
    const rid = await ensureRoomForRequest();
    if (!rid) return;
    // Session-41: for withdraw with no saved account, require either full
    // bank fields OR a UPI id in the inline account form.
    let newAccountPayload = undefined;
    if (chatType === 'withdrawal' && !srPrimaryAccount) {
      const na = srNewAcc;
      const hasBank = na.account_number.trim() && na.ifsc.trim();
      const hasUpi = na.upi_id.trim();
      if (!hasBank && !hasUpi) {
        toast.error('Enter bank details (A/c + IFSC) or a UPI ID');
        return;
      }
      newAccountPayload = {
        holder_name: na.holder_name.trim() || undefined,
        account_number: na.account_number.trim() || undefined,
        ifsc: na.ifsc.trim().toUpperCase() || undefined,
        bank: na.bank.trim() || undefined,
        upi_id: na.upi_id.trim() || undefined,
      };
    }
    setSrBusy(true);
    try {
      const backendType = chatType === 'withdrawal' ? 'withdraw' : chatType;
      const resp = await axios.post(`${API}/service-requests/create`, {
          type: backendType,
          room_id: rid,
          actor_id: user.id,
          amount: srAmount ? Number(srAmount) : undefined,
          reference: srReference.trim() || undefined,
          new_account: newAccountPayload,
        });
      setShowSRModal(false);
      setSrAmount(''); setSrReference('');
      setSrNewAcc({ holder_name: '', account_number: '', ifsc: '', bank: '', upi_id: '' });
      // Fix doc #7 — show the created request card immediately (+ delayed safety net)
      setTimeout(() => loadMessagesRef.current(rid), 300);
      setTimeout(() => loadMessagesRef.current(rid), 1200);
      // Session-42: use client-bank-details as single source of truth. After
      // saving, refetch to display the new primary card on the next open.
      if (newAccountPayload) {
        try {
          const res = await axios.get(`${API}/client-bank-details/${user.id}`);
          const d = res.data?.bank_details || {};
          if (d.bank_account_number || d.upi_id || d.account_holder_name) {
            setSrPrimaryAccount({
              bank: d.bank_name || '',
              account_number: d.bank_account_number || '',
              ifsc: d.ifsc_code || '',
              holder_name: d.account_holder_name || '',
              upi_id: d.upi_id || '',
              is_primary: true,
            });
          }
        } catch { /* silent */ }
      }
      toast.success('✅ Request raised');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to raise request');
    } finally { setSrBusy(false); }
  };

  // Session-40 → Session-42: fetch saved bank details for withdraw modal preview.
  // SINGLE SOURCE OF TRUTH: reads from `client_bank_details` — the same
  // collection handler/admin edit from the client-edit page. Whatever they
  // save (marked primary) shows up here automatically.
  useEffect(() => {
    if (chatType !== 'withdrawal' || !user?.id) return;
    axios.get(`${API}/client-bank-details/${user.id}`)
      .then(res => {
        const d = res.data?.bank_details || {};
        // Consider "saved" if any identifying field exists
        const hasAny = !!(d.bank_account_number || d.upi_id || d.account_holder_name);
        if (!hasAny) { setSrPrimaryAccount(null); return; }
        setSrPrimaryAccount({
          bank: d.bank_name || '',
          account_number: d.bank_account_number || '',
          ifsc: d.ifsc_code || '',
          holder_name: d.account_holder_name || '',
          upi_id: d.upi_id || '',
          is_primary: true,
        });
      })
      .catch(() => setSrPrimaryAccount(null));
  }, [chatType, user?.id]);

  // Point 1 — attempts to actually deliver one locally-queued Support
  // message. Used both by the initial send and by silent background
  // retries, so a network blip never loses the message — it just stays
  // queued (in state AND localStorage) until it goes through.
  const attemptSendSupportMessage = async (item) => {
    try {
      const messageData = {
        room_id: item.roomId,
        sender_id: item.senderId,
        text: item.text,
        image_url: null,
        voice_url: null,
        client_message_id: item.clientMessageId,
      };
      if (item.replyTo) messageData.reply_to = item.replyTo;
      const response = await axios.post(`${API}/chat/send-message`, messageData);

      notifiedMessageIds.current.add(response.data.id);
      recentlyAddedMsgIds.current.add(response.data.id);
      setTimeout(() => recentlyAddedMsgIds.current.delete(response.data.id), 3000);
      setMessages(prev => {
        const withoutOptimistic = prev.filter(m => m.id !== `optimistic_${item.clientMessageId}`);
        if (withoutOptimistic.some(m => m.id === response.data.id)) return withoutOptimistic;
        return [...withoutOptimistic, response.data];
      });
      _removeFromSupportQueue(item.roomId, item.clientMessageId);

      // Bot may auto-post options after our message — refresh a couple of
      // times so it appears instantly even if the socket is slow (mirrors
      // the original non-queued support-send flow).
      setTimeout(() => loadMessagesRef.current(item.roomId), 400);
      setTimeout(() => loadMessagesRef.current(item.roomId), 1500);
      return true;
    } catch (error) {
      if (error.response) {
        // Server explicitly rejected it (validation, permission, etc.) —
        // not a network blip, so retrying won't help. Surface the failure
        // and stop retrying it.
        setMessages(prev => prev.map(m => (
          m.id === `optimistic_${item.clientMessageId}` ? { ...m, pending: false, send_failed: true } : m
        )));
        _removeFromSupportQueue(item.roomId, item.clientMessageId);
        return true;
      }
      // No response at all → offline/network error. Leave it queued; the
      // background retry loop below will try again silently.
      return false;
    }
  };

  // Point 1/3 — local-first send for Support chat: the bubble appears
  // instantly with a single grey "sending" tick (via ReadReceipts reading
  // `pending`) regardless of connectivity, and the actual delivery attempt
  // (with silent retry on failure) happens in the background.
  const sendSupportMessageLocalFirst = (messageText, replyToSnapshot) => {
    const clientMessageId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `cmid_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    const replyTo = replyToSnapshot ? {
      message_id: replyToSnapshot.id,
      text: replyToSnapshot.text || '(Media)',
      sender_name: replyToSnapshot.sender_name,
    } : undefined;

    const optimisticMsg = {
      id: `optimistic_${clientMessageId}`,
      client_message_id: clientMessageId,
      room_id: roomId,
      sender_id: user.id,
      sender_role: user.role,
      sender_name: user.username || user.personal_name || 'You',
      text: messageText,
      image_url: null,
      voice_url: null,
      timestamp: new Date().toISOString(),
      pending: true,
      reply_to: replyTo,
    };
    setMessages(prev => [...prev, optimisticMsg]);
    setIsUserScrolling(false);

    if (typingTimeout) clearTimeout(typingTimeout);
    sendTypingIndicator(roomId, user.id, false);
    axios.post(`${API}/chat/typing?user_id=${user.id}`, { room_id: roomId, is_typing: false }).catch(() => {});

    const item = { clientMessageId, roomId, senderId: user.id, text: messageText, replyTo };
    _saveSupportQueue(roomId, [..._loadSupportQueue(roomId), item]);
    attemptSendSupportMessage(item);
  };

  // Point 1 — silent background retry for anything still stuck in the
  // Support send queue, plus resuming anything left queued from a previous
  // session/page-reload so a message the customer sent while offline never
  // just disappears.
  useEffect(() => {
    if (chatType !== 'support' || !roomId) return;

    const resumeQueued = () => {
      const queued = _loadSupportQueue(roomId);
      if (!queued.length) return;
      setMessages(prev => {
        const existingIds = new Set(prev.map(m => m.client_message_id).filter(Boolean));
        const toAdd = queued
          .filter(item => !existingIds.has(item.clientMessageId))
          .map(item => ({
            id: `optimistic_${item.clientMessageId}`,
            client_message_id: item.clientMessageId,
            room_id: item.roomId,
            sender_id: item.senderId,
            sender_role: user?.role,
            sender_name: user?.username || user?.personal_name || 'You',
            text: item.text,
            image_url: null,
            voice_url: null,
            timestamp: new Date().toISOString(),
            pending: true,
            reply_to: item.replyTo,
          }));
        return toAdd.length ? [...prev, ...toAdd] : prev;
      });
      queued.forEach(item => attemptSendSupportMessage(item));
    };

    resumeQueued();
    window.addEventListener('online', resumeQueued);
    const retryTimer = setInterval(resumeQueued, 15000);
    return () => {
      window.removeEventListener('online', resumeQueued);
      clearInterval(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatType, roomId]);

  const sendMessage = async (e, textFromInput = null) => {
    e?.preventDefault();

    // Use text from parameter (WhatsAppInput) or from inputRef (form submission)
    const messageText = textFromInput || inputRef.current?.value?.trim();

    if (!messageText) {
      return;
    }

    // IMMEDIATELY clear input (before API call)
    // Note: WhatsAppInput handles its own clearing via handleSend

    // Point 1/3 — local-first send + persistent offline queue, tightly
    // scoped to the Support chat type only, and only once a room already
    // exists (a brand-new guest/inquiry session's very first message still
    // needs the sequential session-bootstrap flow below since there's no
    // room yet to attach a locally-queued message to).
    if (chatType === 'support' && roomId && !user?.is_pending) {
      sendSupportMessageLocalFirst(messageText, replyingTo);
      setReplyingTo(null);
      return;
    }

    try {
      let currentRoomId = roomId;
      let currentUser = user;
      
      // If this is a pending inquiry session, create it NOW on first message
      if (user?.is_pending && user?.is_inquiry) {
        console.log('📝 Creating inquiry session on first message...');
        
        const startResponse = await axios.post(`${API}/inquiry/start`, {
          ip_address: user.ip_address,
          device_id: user.device_id || localStorage.getItem('dws_device_id') || '',
          referrer: user.referrer
        });
        
        const { guest_name, guest_user_id, chat_room_id } = startResponse.data;
        
        // Update localStorage with real session
        const session = {
          guest_name,
          guest_user_id,
          chat_room_id,
          ip_address: user.ip_address,
          is_inquiry: true
        };
        localStorage.setItem('inquiry_session', JSON.stringify(session));
        
        // Update guest user state
        const newGuestUser = {
          id: guest_user_id,
          username: guest_name,
          phone: `GUEST_${guest_name}`,
          role: 'customer',
          is_guest: true,
          is_inquiry: true
        };
        setGuestUser(newGuestUser);
        currentUser = newGuestUser;
        currentRoomId = chat_room_id;
        setRoomId(currentRoomId);
        
        console.log('✅ Inquiry session created:', guest_name);
      }
      
      // If this is a pending support guest (Trouble Sign In), create on first message
      if (user?.is_pending && user?.is_support_guest) {
        console.log('📝 Creating support guest session on first message...');
        const deviceId = user.device_id || localStorage.getItem('dws_device_id') || '';
        const startResponse = await axios.post(`${API}/guest-support/start`, { device_id: deviceId });
        const { guest_user_id, guest_name, chat_room_id } = startResponse.data;
        const newGuestUser = {
          id: guest_user_id,
          username: guest_name,
          phone: `GUEST_${guest_name}`,
          role: 'customer',
          is_guest: true,
          is_support_guest: true
        };
        setGuestUser(newGuestUser);
        currentUser = newGuestUser;
        currentRoomId = chat_room_id;
        setRoomId(currentRoomId);
        console.log('✅ Support guest session created:', guest_name);
      }
      
      // If no room exists yet (for regular customers), create it on first message
      if (!currentRoomId) {
        const roomResponse = await axios.post(`${API}/chat/create-room`, {
          customer_id: currentUser.id,
          chat_type: chatType,
          ip_address: null
        });
        
        currentRoomId = roomResponse.data.room_id;
        setRoomId(currentRoomId);
      }
        
        // Phase 2: Auto-assign handler
        try {
          await axios.post(`${API}/chat/auto-assign/${currentRoomId}`);
          console.log('✅ Handler auto-assigned');
        } catch (err) {
          console.error('Auto-assign failed:', err);
        }

      
      // Prepare message data
      const messageData = {
        room_id: currentRoomId,
        sender_id: currentUser.id,
        text: messageText,
        image_url: null,
        voice_url: null
      };

      // Add reply reference if replying to a message
      if (replyingTo) {
        messageData.reply_to = {
          message_id: replyingTo.id,
          text: replyingTo.text || '(Media)',
          sender_name: replyingTo.sender_name
        };
      }

      const response = await axios.post(`${API}/chat/send-message`, messageData);
      
      // Clear reply state after sending
      setReplyingTo(null);
      
      // Phase 2: Stop typing indicator when message sent
      if (typingTimeout) {
        clearTimeout(typingTimeout);
      }
      if (roomId) {
        sendTypingIndicator(roomId, user.id, false);
        axios.post(`${API}/chat/typing?user_id=${user.id}`, {
          room_id: roomId,
          is_typing: false
        }).catch(err => console.error('Typing update failed:', err));
      }

      // Add message to local state (dedup: Socket.IO may deliver faster)
      notifiedMessageIds.current.add(response.data.id);
      recentlyAddedMsgIds.current.add(response.data.id);
      setTimeout(() => recentlyAddedMsgIds.current.delete(response.data.id), 3000);
      setMessages(prev => {
        if (prev.some(m => m.id === response.data.id)) return prev;
        return [...prev, response.data];
      });
      setIsUserScrolling(false);

      // In support chat, bot may auto-post options after our message.
      // Refresh a couple of times so the bot bubble appears instantly even if socket is slow.
      if (chatType === 'support' && currentRoomId) {
        setTimeout(() => loadMessagesRef.current(currentRoomId), 400);
        setTimeout(() => loadMessagesRef.current(currentRoomId), 1500);
      }

      console.log('✅ Message sent:', messageText);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  // Message interaction handlers
  const touchStartPos = useRef(null);
  
  const handleTouchStart = (messageId, e) => {
    // Don't trigger on child interactive elements
    if (e.target.tagName === 'IMG' || e.target.tagName === 'AUDIO' || e.target.closest('audio')) {
      return;
    }
    
    // Check if touches array exists and has elements
    if (!e.touches || e.touches.length === 0) {
      console.log('No touch data available');
      return;
    }
    // Skip long-press during pinch or when page is zoomed — pan-then-hold
    // must not accidentally open the reactions/delete bar.
    if (e.touches.length > 1) return;
    try {
      const vv = window.visualViewport;
      if (vv && vv.scale > 1.05) return;
    } catch (_) {}
    
    // Store initial touch position
    const touch = e.touches[0];
    touchStartPos.current = {
      x: touch.clientX,
      y: touch.clientY
    };
    
    console.log('Touch start on message:', messageId, 'at', touch.clientX, touch.clientY);
    
    longPressTimer.current = setTimeout(() => {
      const pos = touchStartPos.current;
      if (!pos) return;
      console.log('Long press detected! Opening menu at:', pos.x, pos.y);
      setLongPressMenu({
        messageId,
        x: pos.x,
        y: pos.y
      });
    }, 500); // 500ms hold
  };

  const handleTouchEnd = (e) => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
      console.log('Touch ended - cleared long press timer');
    }
    touchStartPos.current = null;
  };

  const handleTouchMove = (e) => {
    // Cancel long press only if finger moves more than 10px
    if (longPressTimer.current && touchStartPos.current && e.touches && e.touches.length > 0) {
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const deltaX = Math.abs(currentX - touchStartPos.current.x);
      const deltaY = Math.abs(currentY - touchStartPos.current.y);
      
      // If movement is more than 10px, cancel long press
      if (deltaX > 10 || deltaY > 10) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
        console.log('Touch moved too much - cancelled long press');
      }
    }
  };

  const lastTap = useRef(0);
  const handleDoubleTap = (messageId) => {
    const now = Date.now();
    if (now - lastTap.current < 300) {
      setShowEmojiPicker(messageId);
    }
    lastTap.current = now;
  };

  const handleCopyMessage = (messageText) => {
    navigator.clipboard.writeText(messageText).then(() => {
      toast.success('Copied', { duration: 1500, style: { background: 'rgba(37,211,102,0.9)', color: '#fff', fontSize: '13px', padding: '6px 16px', minHeight: 'auto' } });
    }).catch(() => {});
    setLongPressMenu(null);
  };

  const handleDeleteClick = (messageId) => {
    setMessageToDelete(messageId);
    setLongPressMenu(null);
    setShowDeleteModal(true);
  };

  const handleDeleteMessage = async () => {
    if (!messageToDelete) return;

    try {
      await axios.delete(`${API}/chat/message/${messageToDelete}`, {
        params: { user_id: user.id, delete_for_everyone: false }
      });

      setMessages(messages.filter(msg => msg.id !== messageToDelete));
      setShowDeleteModal(false);
      setMessageToDelete(null);
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Failed to delete message');
    }
  };

  // Reply Message Handler
  const handleReplyMessage = (msg) => {
    if (!msg) return;
    setReplyingTo(msg);
    setLongPressMenu(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  // Forward Message Handler (for Handler/Admin only)
  const handleForwardMessage = async (msg) => {
    try {
      setMessageToForward(msg);
      
      // Fetch all customer chats (Handler/Admin can forward to other customers)
      const response = await axios.get(`${API}/chat/rooms`, {
        params: { user_id: user.id, role: user.role }
      });
      
      const otherChats = response.data.filter(chat => chat.id !== roomId);
      setAvailableChats(otherChats);
      setShowForwardModal(true);
      setLongPressMenu(null);
    } catch (error) {
      console.error('Failed to load chats:', error);
      toast.error('Failed to load chats');
    }
  };

  const forwardToChat = async (targetRoomId) => {
    if (!messageToForward) return;

    try {
      await axios.post(`${API}/chat/send-message`, {
          room_id: targetRoomId,
          sender_id: user.id,
          message: messageToForward.is_sticker ? '' : (messageToForward.message || '📷 Forwarded media'),
          image_url: messageToForward.image_url || null,
          voice_url: messageToForward.voice_url || null,
          is_sticker: !!messageToForward.is_sticker
        });

      toast.success('Message forwarded!');
      setShowForwardModal(false);
      setMessageToForward(null);
      setAvailableChats([]);
    } catch (error) {
      console.error('Failed to forward message:', error);
      toast.error('Failed to forward');
    }
  };

  const handleAddReaction = async (messageId, emoji) => {
    try {
      await axios.post(`${API}/chat/message/${messageId}/reaction`, null, {
        params: { user_id: user.id, emoji }
      });

      setMessages(messages.map(msg => {
        if (msg.id === messageId) {
          const reactions = msg.reactions || {};
          if (reactions[emoji] && reactions[emoji].includes(user.id)) {
            reactions[emoji] = reactions[emoji].filter(id => id !== user.id);
            if (reactions[emoji].length === 0) delete reactions[emoji];
          } else {
            if (!reactions[emoji]) reactions[emoji] = [];
            reactions[emoji].push(user.id);
          }
          return { ...msg, reactions };
        }
        return msg;
      }));

      setShowEmojiPicker(null);
    } catch (error) {
      console.error('Failed to add reaction:', error);
    }
  };

  const handleDeleteFullChat = async () => {
    if (!window.confirm('Delete all messages? This cannot be undone.')) return;

    try {
      await axios.delete(`${API}/chat/room/${roomId}/messages`, {
        params: { user_id: user.id }
      });

      setMessages([]);
      setShow3DotMenu(false);
      toast.success('Chat cleared!');
    } catch (error) {
      console.error('Failed to delete chat:', error);
      toast.error('Failed to delete chat');
    }
  };

  const handleMediaSelected = async (media) => {
    try {
      let currentRoomId = roomId;
      
      // If no room exists yet, create it on first message
      if (!currentRoomId) {
        const roomResponse = await axios.post(`${API}/chat/create-room`, {
          customer_id: user.id,
          chat_type: chatType,
          ip_address: null
        });
        
        currentRoomId = roomResponse.data.room_id;
        setRoomId(currentRoomId);
      }
      
      // Determine text and URL based on media type
      let messageText = '';
      let imageUrl = null;
      let voiceUrl = null;
      let documentUrl = null;
      let documentName = null;
      
      if (media.type === 'image') {
        messageText = media.caption || '';
        imageUrl = media.url;
      } else if (media.type === 'video') {
        messageText = media.caption || '🎥 Video';
        imageUrl = media.url;
      } else if (media.type === 'voice') {
        messageText = '🎤 Voice';
        voiceUrl = media.url;
      } else if (media.type === 'sticker') {
        messageText = ''; // No text for stickers, just the image
        imageUrl = media.url;
      } else if (media.type === 'document') {
        messageText = '📄 ' + (media.fileName || 'Document');
        documentUrl = media.url;
        documentName = media.fileName || 'Document';
      } else if (media.type === 'text') {
        messageText = media.text || '';
      }
      
      const response = await axios.post(`${API}/chat/send-message`, {
          room_id: currentRoomId,
          sender_id: user.id,
          text: messageText,
          image_url: imageUrl,
          voice_url: voiceUrl,
          document_url: documentUrl,
          document_name: documentName,
          is_sticker: media.type === 'sticker',
          client_message_id: media.client_message_id,
        });

      // Mark as notified to prevent notification sound
      notifiedMessageIds.current.add(response.data.id);
      recentlyAddedMsgIds.current.add(response.data.id);
      setTimeout(() => recentlyAddedMsgIds.current.delete(response.data.id), 3000);
      // Add locally for instant preview (loadMessages will deduplicate via server data)
      setMessages(prev => {
        if (prev.some(m => m.id === response.data.id)) return prev;
        return [...prev, response.data];
      });
      setIsUserScrolling(false);
    } catch (error) {
      console.error('Failed to send media:', error);
      toast.error('Failed to send media');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  // Handler for WhatsAppInput typing indicator
  const handleTypingIndicator = (isTyping) => {
    if (roomId && user?.id) {
      // Socket.IO real-time typing indicator
      sendTypingIndicator(roomId, user.id, isTyping);
      // HTTP API fallback
      axios.post(`${API}/chat/typing?user_id=${user.id}`, {
        room_id: roomId,
        is_typing: isTyping
      }).catch(err => console.error('Typing update failed:', err));
      
      if (isTyping) {
        if (typingTimeout) clearTimeout(typingTimeout);
        
        const timeout = setTimeout(() => {
          sendTypingIndicator(roomId, user.id, false);
          axios.post(`${API}/chat/typing?user_id=${user.id}`, {
            room_id: roomId,
            is_typing: false
          }).catch(err => console.error('Typing stop failed:', err));
        }, 3000);
        
        setTypingTimeout(timeout);
      }
    }
  };

  // Handler for WhatsAppInput send message
  const handleWhatsAppSendMessage = (text) => {
    sendMessage(null, text);
    // Hide quick replies after first message sent
    setShowQuickReplies(false);
  };

  // Loading state removed - UI renders instantly with cached/empty messages

  return (
    <div ref={containerRef} className="fixed inset-0 bg-gray-100 overflow-hidden">
      {/* Header - WhatsApp Style */}
      <div ref={chatHeaderRef} className="absolute top-0 left-0 right-0 bg-white border-b shadow-sm z-50">
        <div className="flex items-center min-h-[56px] px-2">
          {/* Back Button - Always visible */}
          <button
            onClick={() => {
              // If embedded mode with callback, use it
              if (isEmbedded && onBack) {
                onBack();
                return;
              }
              // Support-restricted (device recovery): back = logout to login page.
              if (user?.support_restricted) {
                logout(true);
                return;
              }
              // Guest users go back to customer login, regular users go to customer-select
              if (user?.is_guest || user?.is_inquiry) {
                navigate('/customer-login');
              } else {
                navigate('/customer-select');
              }
            }}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-700"
            aria-label="Go back"
          >
            <ArrowLeft size={24} />
          </button>
          
          {/* Profile Picture - WhatsApp Style */}
          {user?.is_inquiry ? (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden ml-2">
              {getDisplayDP() ? (
                <img 
                  src={`${API.replace(/\/api\/?$/, '')}/api${getDisplayDP()}`} 
                  alt="Support" 
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <Headset size={24} weight="fill" className="text-gray-600" />
              )}
            </div>
          ) : (
            <button
              onClick={() => setShowStatusViewer(true)}
              className="relative flex-shrink-0 group ml-1"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 p-0.5">
                <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                  {getDisplayDP() ? (
                    <img 
                      src={`${API.replace(/\/api\/?$/, '')}/api${getDisplayDP()}`} 
                      alt="Support" 
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <Headset size={24} weight="fill" className="text-gray-700" />
                  )}
                </div>
              </div>
            </button>
          )}
          
          {/* Name and Status - Dynamic based on settings */}
          <div className="flex-1 ml-3 min-w-0">
            <h2 className="text-base font-semibold text-gray-900 truncate">{getDisplayName()}</h2>
            <div className="flex items-center">
              <OnlineStatus isOnline={handlerOnlineStatus.is_online} lastSeen={handlerOnlineStatus.last_seen} showText={true} textColor="text-gray-500" textSize="text-xs" />
            </div>
            {!isOnline && (
              <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold" data-testid="offline-cache-indicator">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                Offline — showing saved chats
              </span>
            )}
          </div>

          {/* Audio Call Button - Redirect to Phone Dialpad */}
          <button
            onClick={() => {
              // Customer: redirect to support phone number
              if (supportInfo.phone) {
                window.location.href = `tel:${supportInfo.phone}`;
              } else {
                toast.error('Support phone number not available');
              }
            }}
            className="p-2 hover:bg-gray-100 rounded-full text-gray-700"
            data-testid="audio-call-btn"
          >
            <Phone size={22} />
          </button>

          {/* 3-Dot Menu — hidden for support-restricted (device recovery) users:
              no settings/profile/logout while restricted. Back = logout. */}
          {!user?.support_restricted && (
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setShow3DotMenu(!show3DotMenu)}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-700"
            >
              <DotsThreeVertical size={24} />
            </button>
            
            {show3DotMenu && (
              <>
              <div className="fixed inset-0 z-40" onClick={() => setShow3DotMenu(false)} />
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-2 z-50">
                <button
                  onClick={() => {
                    setShow3DotMenu(false);
                    setShowStatusViewer(true);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center gap-3 border-b"
                >
                  <User size={22} className="text-purple-600" weight="fill" />
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-800">View Status</span>
                    <span className="text-xs text-gray-500">See handler status</span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setShow3DotMenu(false);
                    setShowSupportMenu(true);
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center gap-3 border-b"
                >
                  <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                  </svg>
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-800">Support</span>
                    <span className="text-xs text-gray-500">Call, WhatsApp, Chat</span>
                  </div>
                </button>
                <button
                  onClick={async () => {
                    setShow3DotMenu(false);
                    try {
                      const next = !isChatMuted;
                      await axios.post(`${API}/chat/room/${roomId}/mute`, { user_id: user.id, is_muted: next });
                      setIsChatMuted(next);
                      toast(next ? 'Chat muted' : 'Chat unmuted', { duration: 1500 });
                    } catch (e) { toast.error('Failed to update mute setting'); }
                  }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center gap-3 border-b"
                  data-testid="mute-chat-btn"
                >
                  {isChatMuted ? <BellSlash size={22} className="text-gray-500" /> : <Bell size={22} className="text-gray-500" />}
                  <span className="font-semibold text-gray-800">{isChatMuted ? 'Unmute Chat' : 'Mute Chat'}</span>
                </button>
                <button
                  onClick={handleDeleteFullChat}
                  className="w-full px-4 py-3 text-left text-red-600 hover:bg-gray-100 flex items-center gap-3"
                >
                  <Trash size={22} weight="fill" />
                  <span className="font-semibold">Clear Chat</span>
                </button>
                {/* Withdraw / Deposit history (last 5) — Task B */}
                <button
                  onClick={() => { setShow3DotMenu(false); setHistoryModal('withdraw'); }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center gap-3 border-t"
                  data-testid="client-withdraw-history-btn"
                >
                  <span className="text-xl">💸</span>
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-800">Withdraw History</span>
                    <span className="text-xs text-gray-500">Last 5 paid withdrawals</span>
                  </div>
                </button>
                <button
                  onClick={() => { setShow3DotMenu(false); setHistoryModal('deposit'); }}
                  className="w-full px-4 py-3 text-left hover:bg-gray-100 flex items-center gap-3 border-t"
                  data-testid="client-deposit-history-btn"
                >
                  <span className="text-xl">💰</span>
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-800">Deposit History</span>
                    <span className="text-xs text-gray-500">Last 5 deposits</span>
                  </div>
                </button>
                <ThemeToggleMenuItem onClick={() => setShow3DotMenu(false)} />
                {chatType === 'support' && (
                  <button
                    onClick={async () => {
                      setShow3DotMenu(false);
                      try {
                        await axios.post(`${API}/support/session/close`, { customer_id: user?.id });
                        if (roomId) loadMessagesRef.current(roomId);
                      } catch (e) {
                        console.error('End chat failed', e);
                      }
                    }}
                    className="w-full px-4 py-3 text-left text-blue-600 hover:bg-gray-100 flex items-center gap-3 border-t"
                    data-testid="end-support-chat-btn"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <div className="flex flex-col">
                      <span className="font-semibold">End This Chat</span>
                      <span className="text-xs text-gray-500">Reset options for new query</span>
                    </div>
                  </button>
                )}
              </div>
              </>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Session-40: Raise Service Request CTA — customer initiates a
          Deposit / Withdraw / Support request from the top of the chat.
          Regular chit-chat no longer auto-creates requests. */}
      {!user?.is_guest && !user?.is_inquiry && !user?.support_restricted && (chatType === 'deposit' || chatType === 'withdrawal' || chatType === 'support') && (
        <div
          className="absolute left-0 right-0 bg-white border-b border-gray-200 px-3 py-2 flex items-center gap-2 z-40"
          style={{ top: '56px' }}
        >
          <button
            onClick={() => { setSrAmount(''); setSrReference(''); setShowSRModal(true); }}
            className={`flex-1 py-2 rounded-lg text-white text-sm font-semibold ${
              chatType === 'deposit' ? 'bg-blue-600 hover:bg-blue-700'
              : chatType === 'withdrawal' ? 'bg-emerald-600 hover:bg-emerald-700'
              : 'bg-purple-600 hover:bg-purple-700'
            }`}
            data-testid="customer-raise-request-btn"
          >
            {/* Session-49: honour admin-configured Chat Header Label. If admin
                left it blank, fall back to the emoji-flavoured hardcoded
                default so the button never looks empty. */}
            {hdrLabels.left_create
              ? hdrLabels.left_create
              : (chatType === 'deposit' ? '💰 Raise Deposit Request'
                : chatType === 'withdrawal' ? '📤 Raise Withdraw Request'
                : '🆘 Raise Support Request')}
          </button>
        </div>
      )}

      {/* Session-40: request creation modal */}
      {showSRModal && (
        <DraggableModal
          open={showSRModal}
          onClose={() => { if (!srBusy) { setShowSRModal(false); setSrAmount(''); setSrReference(''); } }}
          testId="sr-create-modal"
          title={chatType === 'deposit' ? '💰 Deposit Request'
            : chatType === 'withdrawal' ? '📤 Withdraw Request'
            : '🆘 Support Request'}
        >
          <div>
            {chatType !== 'support' && (
              <div className="mb-3">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Amount (₹)</label>
                <input
                  type="number" value={srAmount}
                  onChange={(e) => setSrAmount(e.target.value)}
                  placeholder="e.g. 2000"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  data-testid="sr-amount-input"
                />
                {/* Session-48: Admin-configured quick chips. Click = instant
                    request send (no submit needed). Text-kind chips send with
                    a label like "Send QR" instead of amount. */}
                {(() => {
                  const enabled = chatType === 'deposit' ? srPresets.deposit_enabled : srPresets.withdraw_enabled;
                  const list = chatType === 'deposit' ? srPresets.deposit_presets : srPresets.withdraw_presets;
                  if (!enabled || !list || list.length === 0) return null;
                  return (
                    <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid="sr-quick-chips">
                      {list.map(p => (
                        <button
                          key={p.id}
                          disabled={srBusy}
                          onClick={() => submitSRQuickChip(p)}
                          data-testid={`sr-chip-${p.label.replace(/\s+/g,'-').toLowerCase()}`}
                          className={`px-3 py-2 rounded-md text-xs font-semibold border transition ${p.kind === 'text' ? 'bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300' : 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-300'} disabled:opacity-50`}
                        >{p.label}</button>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
            {/* Session-48b: Reference field ONLY for Support. Deposit + Withdraw
                are now amount-only (or quick-chip only) — customer just picks
                a chip like "up to 500" / "Send QR" OR types an amount and hits
                Send. No more Amount-OR-Reference confusion. */}
            {chatType === 'support' && (
              <div className="mb-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Query / Message (required)
                </label>
                <textarea
                  value={srReference}
                  onChange={(e) => setSrReference(e.target.value)}
                  placeholder="Describe your issue…"
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  data-testid="sr-reference-input"
                />
                {/* Session-48g: Admin-configured support quick options (text
                    chips). Click = instant support-request send with label
                    as reference (like "Deposit me problem"). */}
                {srPresets.support_enabled && srPresets.support_presets?.length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid="sr-support-chips">
                    {srPresets.support_presets.map(p => (
                      <button
                        key={p.id}
                        disabled={srBusy}
                        onClick={() => submitSRQuickChip({ ...p, kind: 'text' })}
                        data-testid={`sr-support-chip-${p.label.replace(/\s+/g,'-').toLowerCase()}`}
                        className="px-3 py-2 rounded-md text-xs font-semibold border transition bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300 disabled:opacity-50"
                      >{p.label}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {chatType === 'withdrawal' && srPrimaryAccount && (
              <div className="mb-3 p-2 bg-emerald-50 border border-emerald-200 rounded-md text-[12px]" data-testid="sr-primary-account-card">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-emerald-800 mb-0.5">🏦 Primary Account (saved · auto-used)</div>
                  {/* Session-42: Change button — locked, redirects client to
                      Support with a Hindi warning. Change can only be done
                      via handler edit or a support request. */}
                  <button
                    onClick={() => setShowAccChangeWarn(true)}
                    className="text-[10px] px-2 py-0.5 rounded bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100 font-semibold"
                    data-testid="sr-primary-change-btn"
                  >Change</button>
                </div>
                <div className="text-slate-700">{srPrimaryAccount.bank || '—'}{srPrimaryAccount.account_number ? ` · A/c ${srPrimaryAccount.account_number}` : ''}</div>
                <div className="text-slate-600">👤 {srPrimaryAccount.holder_name || '—'}</div>
                <div className="text-slate-500 text-[11px]">
                  {srPrimaryAccount.ifsc ? `IFSC ${srPrimaryAccount.ifsc}` : ''}
                  {srPrimaryAccount.ifsc && srPrimaryAccount.upi_id ? ' · ' : ''}
                  {srPrimaryAccount.upi_id ? `UPI ${srPrimaryAccount.upi_id}` : ''}
                </div>
              </div>
            )}
            {chatType === 'withdrawal' && !srPrimaryAccount && (
              <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-md space-y-2" data-testid="sr-new-account-form">
                <div className="font-semibold text-amber-800 text-[12px]">🏦 Add your withdraw account (saved for future)</div>
                <input type="text" value={srNewAcc.holder_name}
                  onChange={(e) => setSrNewAcc(a => ({ ...a, holder_name: e.target.value }))}
                  placeholder="A/c Holder Name"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs"
                  data-testid="sr-new-acc-holder" />
                <input type="text" value={srNewAcc.account_number}
                  onChange={(e) => setSrNewAcc(a => ({ ...a, account_number: e.target.value.replace(/\D/g, '') }))}
                  placeholder="Bank Account Number"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs"
                  data-testid="sr-new-acc-number" />
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" value={srNewAcc.ifsc}
                    onChange={(e) => setSrNewAcc(a => ({ ...a, ifsc: e.target.value.toUpperCase() }))}
                    placeholder="IFSC"
                    className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs"
                    data-testid="sr-new-acc-ifsc" />
                  <input type="text" value={srNewAcc.bank}
                    onChange={(e) => setSrNewAcc(a => ({ ...a, bank: e.target.value }))}
                    placeholder="Bank Name"
                    className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs"
                    data-testid="sr-new-acc-bank" />
                </div>
                <div className="text-[11px] text-slate-500 -my-1 text-center">— or —</div>
                <input type="text" value={srNewAcc.upi_id}
                  onChange={(e) => setSrNewAcc(a => ({ ...a, upi_id: e.target.value }))}
                  placeholder="UPI ID (e.g. name@ybl)"
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-xs"
                  data-testid="sr-new-acc-upi" />
                <p className="text-[10px] text-amber-700">
                  Fill Account + IFSC <b>OR</b> UPI ID — we'll save it to your profile so future withdraws auto-fill.
                </p>
              </div>
            )}
            {chatType !== 'support' && (
              <p className="text-[11px] text-slate-500 mb-3">Enter an amount OR tap a quick chip above to send.</p>
            )}
            <div className="flex gap-2 justify-end">
              <button disabled={srBusy}
                      onClick={() => { setShowSRModal(false); setSrAmount(''); setSrReference(''); }}
                      className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50">Cancel</button>
              <button disabled={srBusy}
                      onClick={submitSRCreate}
                      className={`px-4 py-1.5 text-sm rounded text-white ${
                        chatType === 'deposit' ? 'bg-blue-600 hover:bg-blue-700'
                        : chatType === 'withdrawal' ? 'bg-emerald-600 hover:bg-emerald-700'
                        : 'bg-purple-600 hover:bg-purple-700'
                      } disabled:opacity-50`}
                      data-testid="sr-submit-btn">
                {srBusy ? 'Sending…' : 'Send Request'}
              </button>
            </div>
          </div>
        </DraggableModal>
      )}

      {/* Session-42: Locked Account "Change" warning — Hindi. Client cannot
          self-change the primary account; must raise a support ticket. */}
      {showAccChangeWarn && (
        <div className="fixed inset-0 bg-black/60 z-[130] flex items-end sm:items-center justify-center p-2"
             onClick={() => setShowAccChangeWarn(false)}
             data-testid="sr-acc-change-warn">
          <div onClick={(e) => e.stopPropagation()}
               className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md p-5 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🔒</span>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-800 mb-1">Account details locked</h3>
                <p className="text-[13px] text-slate-700 leading-relaxed">
                  Account details yahaan se change nahi ho paayenge.<br />
                  Change karvaane ke liye <b>Support section</b> mein jaayein aur wahaan account change ki request raise karein.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowAccChangeWarn(false)}
                className="flex-1 py-2 border border-slate-300 rounded-lg text-sm"
              >Close</button>
              <button
                onClick={() => {
                  // Close both modals then hop to the client's support chat
                  setShowAccChangeWarn(false);
                  setShowSRModal(false);
                  navigate('/customer-chat?type=support');
                }}
                className="flex-1 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold"
                data-testid="sr-acc-change-goto-support"
              >📞 Go to Support</button>
            </div>
          </div>
        </div>
      )}

      {pinnedMessages.length > 0 && (
        <div className="absolute left-0 right-0 bg-white border-b shadow-sm z-20" style={{ top: '72px' }} data-testid="pinned-message-banner">
          {pinnedMessages.map((pm) => (
            <div
              key={pm.id}
              className="px-4 py-2 flex items-center gap-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              onClick={() => {
                const el = document.getElementById(`msg-${pm.id}`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }}
            >
              <PushPin size={18} className="text-blue-500 flex-shrink-0" weight="fill" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-blue-600 font-semibold">Pinned Message</p>
                <p className="text-sm text-gray-600 truncate">{pm.text || '📷 Media'}</p>
              </div>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await axios.delete(`${API}/chat/room/${roomId}/pin/${pm.id}`);
                    setPinnedMessages((prev) => prev.filter((m) => m.id !== pm.id));
                    toast('Unpinned', { duration: 1000 });
                  } catch (err) {}
                }}
                className="p-1 hover:bg-gray-200 rounded-full"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Chat Messages - Fixed position area between header and input */}
      <div 
        ref={chatContainerRef}
        onScroll={handleScroll}
        className="absolute left-0 right-0 overflow-y-auto p-3 bg-[#efeae2]" 
        style={{ 
          top: pinnedMessages.length > 0 ? `${headerHeight + pinnedMessages.length * 56}px` : `${headerHeight}px`,
          bottom: showQuickReplies ? '120px' : '56px',
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'412\' height=\'412\' viewBox=\'0 0 412 412\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23d6d0c5\' fill-opacity=\'0.4\'%3E%3Ccircle cx=\'20\' cy=\'20\' r=\'2\'/%3E%3Ccircle cx=\'100\' cy=\'50\' r=\'1.5\'/%3E%3Ccircle cx=\'200\' cy=\'30\' r=\'2\'/%3E%3Ccircle cx=\'300\' cy=\'60\' r=\'1.5\'/%3E%3Ccircle cx=\'60\' cy=\'120\' r=\'2\'/%3E%3Ccircle cx=\'160\' cy=\'100\' r=\'1.5\'/%3E%3Ccircle cx=\'260\' cy=\'130\' r=\'2\'/%3E%3Ccircle cx=\'360\' cy=\'110\' r=\'1.5\'/%3E%3Ccircle cx=\'40\' cy=\'200\' r=\'2\'/%3E%3Ccircle cx=\'140\' cy=\'220\' r=\'1.5\'/%3E%3Ccircle cx=\'240\' cy=\'190\' r=\'2\'/%3E%3Ccircle cx=\'340\' cy=\'210\' r=\'1.5\'/%3E%3Ccircle cx=\'80\' cy=\'300\' r=\'2\'/%3E%3Ccircle cx=\'180\' cy=\'280\' r=\'1.5\'/%3E%3Ccircle cx=\'280\' cy=\'310\' r=\'2\'/%3E%3Ccircle cx=\'380\' cy=\'290\' r=\'1.5\'/%3E%3Ccircle cx=\'50\' cy=\'380\' r=\'2\'/%3E%3Ccircle cx=\'150\' cy=\'360\' r=\'1.5\'/%3E%3Ccircle cx=\'250\' cy=\'390\' r=\'2\'/%3E%3Ccircle cx=\'350\' cy=\'370\' r=\'1.5\'/%3E%3C/g%3E%3C/svg%3E")'
        }}
      >
        <div className="space-y-[3px] pb-4" style={{ overflowAnchor: 'none' }}>
          {/* Older messages loader (top of chat) */}
          {olderLoading && (
            <div className="flex items-center justify-center py-2" data-testid="older-loading">
              <div className="bg-white/90 text-[#54656f] text-[11px] font-medium px-3 py-1 rounded-full shadow-sm inline-flex items-center gap-2">
                <span className="w-3 h-3 border-2 border-[#54656f] border-t-transparent rounded-full animate-spin"></span>
                Loading older messages…
              </div>
            </div>
          )}
          {!hasMoreOlder && messages.length > 0 && (
            <div className="flex items-center justify-center py-2" data-testid="older-end">
              <div className="bg-white/70 text-[#54656f] text-[10px] font-medium px-3 py-1 rounded-full">
                Start of conversation
              </div>
            </div>
          )}
          {messages.filter(msg => msg && msg.id && !msg.staff_only).map((msg, idx) => {
            const isOwn = msg.sender_id === user?.id;
            const reactions = msg.reactions || {};

            // === Bot message rendering (interactive cards with option buttons) ===
            if (msg.is_bot === true) {
              const suggestedKey = msg.bot_data?.suggested_key;

              // CTA link (open deposit/withdrawal chat)
              if (msg.bot_type === 'cta' && msg.bot_data?.cta_type === 'open_chat') {
                return (
                  <div key={idx} className="flex justify-start my-2" data-testid={`bot-cta-${msg.bot_data.chat_type}`}>
                    <div className="max-w-[85%] bg-white border border-teal-200 rounded-xl shadow-sm p-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center text-white text-xs font-bold">🤖</div>
                        <span className="text-xs font-semibold text-teal-700">Support Bot</span>
                      </div>
                      <p className="text-sm text-gray-800 mb-2">{msg.text}</p>
                      <button
                        onClick={async () => {
                          try {
                            // Post handoff summary to target chat (adds context for handler)
                            await axios.post(`${API}/support/handoff-summary`, {
                              customer_id: user?.id,
                              target_chat_type: msg.bot_data.chat_type,
                            });
                          } catch (e) {
                            console.error('Handoff summary failed', e);
                          }
                          navigate(`/customer-chat?type=${msg.bot_data.chat_type}`);
                        }}
                        className="w-full text-sm px-3 py-2 rounded-lg bg-gradient-to-r from-teal-600 to-emerald-600 text-white font-bold hover:from-teal-700 hover:to-emerald-700 shadow"
                        data-testid={`open-chat-${msg.bot_data.chat_type}`}
                      >
                        {msg.bot_data.label || `Open ${msg.bot_data.chat_type} chat`}
                      </button>
                      <div className="text-[10px] text-gray-400 mt-1.5">
                        {msg.timestamp ? new Date((/[Zz]|[+-]\d\d:?\d\d$/.test(msg.timestamp) ? msg.timestamp : (msg.timestamp||'')+'Z')).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : ''}
                      </div>
                    </div>
                  </div>
                );
              }

              // Handoff summary — HIDDEN from customer (only admins/handlers should see this)
              if (msg.bot_type === 'handoff_summary') {
                return null;
              }

              return (
                <div key={idx} className="flex justify-start my-2" data-testid={`bot-msg-${msg.bot_type || 'text'}`}>
                  <div className="max-w-[85%] bg-white border border-teal-200 rounded-xl shadow-sm p-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center text-white text-xs font-bold">🤖</div>
                      <span className="text-xs font-semibold text-teal-700">Support Bot</span>
                    </div>
                    <p className="text-sm text-gray-800 whitespace-pre-line">{msg.text}</p>

                    {/* Suggested hint */}
                    {suggestedKey && msg.bot_type === 'options' && (
                      <div className="mt-2 mb-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 flex items-center gap-1">
                        <span>💡</span>
                        <span>आपके message के हिसाब से highlighted option देखें</span>
                      </div>
                    )}

                    {/* Options list */}
                    {(msg.bot_type === 'options' || msg.bot_type === 'sub_options' || msg.bot_type === 'resolution') && msg.bot_data?.options && (
                      <div className="mt-2 flex flex-col gap-1.5">
                        {msg.bot_data.options.map((opt) => {
                          const isSuggested = opt.key === suggestedKey && msg.bot_type === 'options';
                          return (
                          <button
                            key={opt.key}
                            onClick={async () => {
                              setBotTyping(true);
                              try {
                                if (msg.bot_type === 'resolution') {
                                  await axios.post(`${API}/support/resolution`, {
                                    session_id: msg.bot_session_id,
                                    resolved: opt.key === 'yes',
                                  });
                                } else if (msg.bot_type === 'sub_options') {
                                  await axios.post(`${API}/support/bot/action`, {
                                    session_id: msg.bot_session_id,
                                    option_key: msg.bot_data.parent_option || 'talk_person',
                                    sub_option: opt.key,
                                  });
                                } else {
                                  await axios.post(`${API}/support/bot/action`, {
                                    session_id: msg.bot_session_id,
                                    option_key: opt.key,
                                  });
                                }
                                // Instant refresh (fallback if socket is slow / disconnected)
                                if (roomId) {
                                  await loadMessagesRef.current(roomId);
                                }
                              } catch (e) {
                                console.error('Bot action failed', e);
                              } finally {
                                setTimeout(() => setBotTyping(false), 500);
                              }
                            }}
                            className={`text-left text-sm px-3 py-2 rounded-lg border font-medium transition-all ${
                              isSuggested
                                ? 'border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-900 ring-2 ring-amber-300 shadow-md animate-pulse'
                                : 'border-teal-200 bg-teal-50 hover:bg-teal-100 text-teal-800'
                            }`}
                            data-testid={`bot-opt-${opt.key}`}
                          >
                            {isSuggested && '⭐ '}
                            {opt.label}
                          </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="text-[10px] text-gray-400 mt-1.5">
                      {msg.timestamp ? new Date((/[Zz]|[+-]\d\d:?\d\d$/.test(msg.timestamp) ? msg.timestamp : (msg.timestamp||'')+'Z')).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : ''}
                    </div>
                  </div>
                </div>
              );
            }

            // Skip if part of image group but not first
            if (isPartOfImageGroup(messages, idx)) return null;
            let imageGroup = getImageGroup(messages, idx);
            // Session-53 defence-in-depth — render-time filter (same as HandlerChat).
            if (imageGroup) {
              imageGroup = imageGroup.filter(g => !isStickerLike(g));
              if (imageGroup.length < 2) imageGroup = null;
            }
            
            // Check if this is the first unread INCOMING message (not own message)
            const isIncoming = msg.sender_id !== user?.id;
            const isPreviousIncoming = idx > 0 ? messages[idx - 1]?.sender_id !== user.id : false;
            const isFirstUnread = isIncoming && !msg.read && (idx === 0 || messages[idx - 1]?.read !== false || !isPreviousIncoming);
            const unreadCount = messages.filter(m => m.sender_id !== user.id && !m.read).length;
            
            return (
              <React.Fragment key={idx}>
                {/* Date Separator - WhatsApp Style */}
                {shouldShowDateSeparator(messages, idx) && (
                  <div className="flex items-center justify-center my-3">
                    <div className="bg-white/90 text-[#54656f] text-[11px] font-medium px-3 py-1 rounded-lg shadow-sm" data-testid="date-separator">
                      {getDateLabel(msg.timestamp)}
                    </div>
                  </div>
                )}

                {/* Unread Messages Divider - Only for incoming messages */}
                {isFirstUnread && unreadCount > 0 && (
                  <div className="flex items-center justify-center my-4">
                    <div className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
                      {unreadCount} Unread Message{unreadCount > 1 ? 's' : ''}
                    </div>
                  </div>
                )}
                
              <div id={`msg-${msg.id}`} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                <div 
                  className="relative max-w-[85%] group"
                  onMouseEnter={() => setHoveredMsg(msg.id)}
                  onMouseLeave={() => setHoveredMsg(null)}
                >
                  {/* Arrow for ALL messages - received messages on right, own messages on right too */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      const bubbleEl = e.target.closest('.group');
                      const bubbleRect = bubbleEl ? bubbleEl.getBoundingClientRect() : null;
                      if (isOwn && bubbleRect) {
                        // Own messages: menu RIGHT edge at bubble LEFT edge
                        setLongPressMenu({
                          messageId: msg.id,
                          x: bubbleRect.left,
                          y: bubbleRect.top + 10,
                          isOwn: true
                        });
                      } else {
                        setLongPressMenu({
                          messageId: msg.id,
                          x: e.clientX,
                          y: e.clientY || 100,
                          isOwn: false
                        });
                      }
                    }}
                    className={`absolute top-1 ${isOwn ? 'right-1' : 'right-1'} z-30 w-7 h-7 flex items-center justify-center rounded-full ${isOwn ? 'bg-[#DCF8C6]/90 hover:bg-[#DCF8C6]' : 'bg-white/90 hover:bg-white'} shadow cursor-pointer`}
                    style={{ opacity: hoveredMsg === msg.id ? 1 : 0, transition: 'opacity 0.15s' }}
                    data-testid="msg-dropdown-arrow"
                  >
                    <CaretDown size={14} className="text-gray-600" weight="bold" />
                  </button>
                  <div 
                    className={`relative ${msg.is_sticker
                      ? 'bg-transparent shadow-none p-0'
                      : `px-2 pt-1.5 pb-[2px] rounded-lg shadow-sm overflow-hidden ${isOwn ? 'bg-[#d9fdd3]' : 'bg-white'}`
                    }`}
                    onContextMenu={(e) => { e.preventDefault(); handleTouchStart(msg.id, e); }}
                    onTouchStart={(e) => handleTouchStart(msg.id, e)}
                    onTouchEnd={handleTouchEnd}
                    onTouchMove={handleTouchMove}
                    onMouseDown={(e) => handleTouchStart(msg.id, { target: e.target, touches: [{ clientX: e.clientX, clientY: e.clientY }] })}
                    onMouseUp={handleTouchEnd}
                    onMouseLeave={handleTouchEnd}
                    onClick={() => handleDoubleTap(msg.id)}
                  >
                    {/* Image Group Grid */}
                    {imageGroup && imageGroup.length > 1 && (
                      <div className="grid grid-cols-2 gap-0.5 rounded-lg overflow-hidden mb-1.5 w-[320px] max-w-full">
                        {imageGroup.slice(0, 4).map((gMsg, gIdx) => {
                          const gUrl = gMsg.image_url.startsWith('http') ? gMsg.image_url 
                            : gMsg.image_url.startsWith('/api/') ? `${BACKEND_URL}${gMsg.image_url}` 
                            : `${BACKEND_URL}/api${gMsg.image_url}`;
                          return (
                            <div key={gIdx} className="relative cursor-pointer" onClick={() => handleImageClick(gUrl)}>
                              <img src={gUrl} alt="" className="w-full h-[120px] object-cover" />
                              {gIdx === 3 && imageGroup.length > 4 && (
                                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                  <span className="text-white text-2xl font-bold">+{imageGroup.length - 4}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Group caption (last photo's caption shown below grid) */}
                    {imageGroup && imageGroup.length > 1 && (() => {
                      const lastMsg = imageGroup[imageGroup.length - 1];
                      const captionText = lastMsg.caption || (lastMsg.text && lastMsg.text.trim() && lastMsg.text !== '🖼️ Photo' && lastMsg.text !== '🎥 Video' ? lastMsg.text : '');
                      return captionText ? <p className="text-sm mb-1">{captionText}</p> : null;
                    })()}

                    {/* Point 63 — view-once placeholder */}
                    {msg.view_once_state && !imageGroup && msg.sender_id !== user.id && msg.view_once_media_type !== 'voice' && (
                      <ViewOnceBubble msg={msg} userId={user.id} openUrl={`${API}/chat/message/${msg.id}/view-once/open`} />
                    )}

                    {/* Single Image or Video */}
                    {msg.image_url && !imageGroup && (
                      msg.is_sticker ? (
                        <img
                          src={buildMediaUrl(msg.image_url)}
                          alt="Sticker"
                          className="w-[88px] max-w-[88px] h-auto object-contain block bg-transparent"
                          data-testid="sticker-message"
                        />
                      ) : isVideoUrl(msg.image_url) ? (
                        <video
                          src={buildMediaUrl(msg.image_url)}
                          controls
                          className="rounded-lg max-w-[250px] max-h-[300px] mb-1.5"
                          preload="metadata"
                          data-testid="video-message"
                        />
                      ) : (
                        <img
                          src={buildMediaUrl(msg.image_url)}
                          alt="Shared"
                          className="rounded-lg w-[180px] h-[180px] object-cover mb-1.5 cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => handleImageClick(buildMediaUrl(msg.image_url))}
                          onError={(e) => {
                            console.error('Image load error:', msg.image_url);
                            e.target.style.display = 'none';
                          }}
                        />
                      )
                    )}
                    
                    {/* Point 63 — view-once voice placeholder */}
                    {msg.view_once_state && msg.sender_id !== user.id && msg.view_once_media_type === 'voice' && (
                      <ViewOnceBubble msg={msg} userId={user.id} openUrl={`${API}/chat/message/${msg.id}/view-once/open`} />
                    )}

                    {/* Voice Message */}
                    {msg.voice_url && (
                      <VoiceMessage
                        audioUrl={(() => {
                          if (msg.voice_url.startsWith('http')) {
                            return msg.voice_url;
                          } else if (msg.voice_url.startsWith('/api/')) {
                            return `${BACKEND_URL}${msg.voice_url}`;
                          } else if (msg.voice_url.startsWith('/uploads/')) {
                            return `${BACKEND_URL}/api${msg.voice_url}`;
                          } else {
                            return `${API}${msg.voice_url}`;
                          }
                        })()}
                        isSender={msg.sender_id === user?.id}
                      />
                    )}
                    
                    {/* Document Display - Clickable */}
                    {msg.document_url && (
                      <a
                        href={(() => {
                          if (msg.document_url.startsWith('http')) {
                            return msg.document_url;
                          } else if (msg.document_url.startsWith('/api/')) {
                            return `${BACKEND_URL}${msg.document_url}`;
                          } else if (msg.document_url.startsWith('/uploads/')) {
                            return `${BACKEND_URL}/api${msg.document_url}`;
                          } else {
                            return `${API}${msg.document_url}`;
                          }
                        })()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText size={24} className="text-white" weight="fill" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {msg.document_name || 'Document'}
                          </p>
                          <p className="text-xs text-gray-500">Tap to open</p>
                        </div>
                      </a>
                    )}
                    
                    {/* Reply Reference - WhatsApp Style */}
                    {msg.reply_to && (
                      <div className={`mb-1 pl-2 py-0.5 border-l-2 rounded text-xs ${
                        isOwn ? 'border-green-700 bg-green-100/30' : 'border-blue-600 bg-blue-100/30'
                      }`}>
                        <div className="font-semibold text-[10px] opacity-70">
                          {msg.reply_to.sender_name}
                        </div>
                        <div className="truncate text-[11px] opacity-60">
                          {msg.reply_to.text || '📷 Media'}
                        </div>
                      </div>
                    )}
                    
                    {/* Call Info Bubble - WhatsApp Style */}
                    {msg.call_info ? (
                      <div className="flex items-center gap-2 py-1">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          msg.call_info.status === 'completed' ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          <Phone size={16} weight="fill" className={`${
                            msg.call_info.status === 'completed' ? 'text-green-600' : 'text-red-500'
                          } ${msg.call_info.caller_id === user?.id ? 'rotate-[135deg]' : ''}`} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">Voice call</p>
                          <p className={`text-xs ${msg.call_info.status === 'completed' ? 'text-gray-500' : 'text-red-500'}`}>
                            {msg.call_info.status === 'completed' 
                              ? (msg.call_info.duration >= 60 ? `${Math.floor(msg.call_info.duration/60)} min` : `${msg.call_info.duration} secs`)
                              : 'No answer'}
                          </p>
                        </div>
                      </div>
                    ) : null}
                    
                    {/* Action Sticker Bubble (Withdraw Done / Deposit Done / Coin Cut) */}
                    {msg.action_sticker && (
                      <div className="py-1"><ActionStickerBubble sticker={msg.action_sticker} /></div>
                    )}
                    
                    {/* Text + WhatsApp-style inline time */}
                    {msg.text && !msg.call_info ? (
                      <div className={`leading-[19px] ${msg.text_striked ? 'bg-red-50 border-l-4 border-red-500 pl-2 pr-1 py-1 rounded' : ''}`} data-testid={msg.text_striked ? `reverted-msg-${msg.id}` : undefined}>
                        {msg.text_striked && (
                          <div className="mb-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold uppercase tracking-wide">
                            ❌ Reverted
                          </div>
                        )}
                        {msg.meta?.bot_options?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid={`bot-options-${msg.id}`}>
                            {msg.meta.bot_options.map((op, oi) => (
                              op.link ? (
                                <a key={oi} href={op.link} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center px-3 py-1.5 rounded-full bg-emerald-500 text-white text-[12px] font-bold hover:bg-emerald-600 shadow-sm"
                                  data-testid={`bot-option-${msg.id}-${oi}`}>
                                  {op.label || `Option ${oi + 1}`}
                                </a>
                              ) : (
                                <button key={oi}
                                  onClick={(e) => { e.stopPropagation(); if (op.reply || op.label) sendMessage(null, op.reply || op.label); }}
                                  className="inline-flex items-center px-3 py-1.5 rounded-full bg-white border border-emerald-400 text-emerald-800 text-[12px] font-bold hover:bg-emerald-50 shadow-sm"
                                  data-testid={`bot-option-${msg.id}-${oi}`}>
                                  {op.label || `Option ${oi + 1}`}
                                </button>
                              )
                            ))}
                          </div>
                        )}
                        <span className={`text-[14.5px] text-[#111b21] break-words whitespace-pre-line ${msg.text_striked ? 'line-through text-red-800/80' : ''}`}>
                          {msg.text.split(/(https?:\/\/[^\s]+)/g).map((part, index) => {
                            if (part.match(/^https?:\/\//)) {
                              return (
                                <a
                                  key={index}
                                  href={part}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[#027eb5] underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {part}
                                </a>
                              );
                            }
                            return part;
                          })}
                        </span>
                        <span className="float-right ml-[8px] mt-[3px] flex items-center gap-[3px] leading-none">
                          {msg.starred_by?.includes(user?.id) && <Star size={11} weight="fill" className="text-yellow-500" />}
                          {msg.edited && <span className="text-[10.5px] text-[#667781] italic">edited</span>}
                          <span className="text-[11px] text-[#667781]">
                            {new Date((/[Zz]|[+-]\d\d:?\d\d$/.test(msg.timestamp) ? msg.timestamp : (msg.timestamp||'')+'Z')).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                          </span>
                          <ReadReceipts message={msg} isSender={isOwn} />
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-end items-center gap-[3px] mt-[2px] mb-[1px]">
                        {msg.starred_by?.includes(user?.id) && <Star size={11} weight="fill" className="text-yellow-500" />}
                        {msg.edited && <span className="text-[10.5px] text-[#667781] italic">edited</span>}
                        <span className="text-[11px] text-[#667781] leading-none">
                          {new Date((/[Zz]|[+-]\d\d:?\d\d$/.test(msg.timestamp) ? msg.timestamp : (msg.timestamp||'')+'Z')).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                        </span>
                        <ReadReceipts message={msg} isSender={isOwn} />
                      </div>
                    )}
                  </div>
                  
                  {/* Reactions */}
                  {Object.keys(reactions).length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {Object.entries(reactions).map(([emoji, userIds]) => (
                        <div 
                          key={emoji}
                          className="bg-white rounded-full px-2 py-1 text-xs shadow-sm border flex items-center gap-1"
                        >
                          <span>{emoji}</span>
                          <span className="text-gray-600">{userIds.length}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Emoji Picker */}
                  {showEmojiPicker === msg.id && (
                    <div className="absolute bottom-full mb-2 bg-white rounded-lg shadow-lg p-2 flex gap-2 z-50">
                      {EMOJIS.map(emoji => (
                        <button
                          key={emoji}
                          onClick={() => handleAddReaction(msg.id, emoji)}
                          className="text-2xl hover:scale-125 transition-transform"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              </React.Fragment>
            );
          })}
          <div ref={messagesEndRef} style={{ overflowAnchor: 'auto', height: '1px' }} />

          {/* Bot Typing Indicator — brief animation after clicking an option */}
          {botTyping && (
            <div className="flex justify-start my-2" data-testid="bot-typing">
              <div className="bg-white border border-teal-200 rounded-xl shadow-sm px-3 py-2 flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-teal-600 flex items-center justify-center text-white text-[10px]">🤖</div>
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-[11px] text-teal-700">Support Bot typing...</span>
              </div>
            </div>
          )}
          
          {/* Typing Indicator */}
          {handlerTyping && (
            <div className="flex justify-start">
              <div className="bg-white px-4 py-2 rounded-lg shadow">
                <TypingIndicator username="Handler" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scroll to Bottom Arrow - WhatsApp Style */}
      {isUserScrolling && (
        <button
          onClick={() => { setIsUserScrolling(false); scrollToBottom(); }}
          className="fixed bottom-24 right-4 z-40 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center border border-gray-200"
          data-testid="scroll-to-bottom-btn"
        >
          <CaretDown size={20} className="text-[#54656f]" weight="bold" />
        </button>
      )}

      {/* Soft nudge (2-4 min) — subtle inline notice */}
      {showSoftNudge && !showTroubleBanner && (
        <div className="fixed bottom-20 left-3 right-3 z-[60] bg-sky-50 border border-sky-200 rounded-lg px-3 py-1.5 flex items-center gap-2 text-xs text-sky-800" data-testid="soft-nudge-banner">
          <span>💬</span>
          <span className="flex-1">Handler को थोड़ा time लग रहा है, please wait...</span>
          <button
            onClick={() => setShowSoftNudge(false)}
            className="text-sky-600 hover:text-sky-900 px-1"
          >
            ✕
          </button>
        </div>
      )}

      {/* Having Trouble Banner - deposit/withdrawal only, when handler hasn't replied for 5+ min */}
      {showTroubleBanner && (
        <div className="fixed bottom-24 left-3 right-3 z-[60] bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-300 rounded-xl shadow-lg px-4 py-3 flex items-center gap-3" data-testid="having-trouble-banner">
          <div className="text-2xl">⏱️</div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-900">Having Trouble?</div>
            <div className="text-xs text-amber-800">बहुत देर हो गई reply आने में। Support team से बात करें?</div>
          </div>
          <button
            onClick={handleEscalateToSupport}
            disabled={escalating}
            className="shrink-0 px-3 py-1.5 rounded-full bg-teal-600 text-white text-xs font-bold hover:bg-teal-700 disabled:opacity-50"
            data-testid="escalate-support-btn"
          >
            {escalating ? '...' : 'Move to Support'}
          </button>
          <button
            onClick={() => setShowTroubleBanner(false)}
            className="shrink-0 text-amber-700 hover:text-amber-900 text-xs px-2"
            data-testid="dismiss-trouble-banner"
          >
            ✕
          </button>
        </div>
      )}

      {/* Input Area - WhatsApp Style Input */}
      <div ref={inputContainerRef} className="absolute bottom-0 left-0 right-0 z-50">
        {/* Quick Reply Suggestions - Above Keyboard */}
        {showQuickReplies && (
          <div className="bg-white border-t border-gray-200 px-3 py-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">Quick Replies</span>
              <button 
                onClick={() => setShowQuickReplies(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X size={16} className="text-gray-400" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {chatType === 'deposit' && (
                <>
                  <button
                    onClick={() => handleWhatsAppSendMessage('Hi, I want Deposit, Please send Qr/Bank Details.')}
                    className="px-3 py-1.5 bg-[#e8f5e9] text-[#128C7E] text-sm rounded-full border border-[#25D366] hover:bg-[#d4edda] transition-colors"
                  >
                    Hi, I want Deposit
                  </button>
                </>
              )}
              {chatType === 'withdrawal' && (
                <>
                  <button
                    onClick={() => handleWhatsAppSendMessage('Hi, I want Withdrawal, Please send Withdrawal Form.')}
                    className="px-3 py-1.5 bg-[#e8f5e9] text-[#128C7E] text-sm rounded-full border border-[#25D366] hover:bg-[#d4edda] transition-colors"
                  >
                    Hi, I want Withdrawal
                  </button>
                </>
              )}
              {chatType === 'support' && (
                <>
                  <button
                    onClick={() => handleWhatsAppSendMessage('Hi, I want Support')}
                    className="px-3 py-1.5 bg-[#e8f5e9] text-[#128C7E] text-sm rounded-full border border-[#25D366] hover:bg-[#d4edda] transition-colors"
                  >
                    Hi, I want Support
                  </button>
                </>
              )}
              {chatType === 'inquiry' && (
                <>
                  <button
                    onClick={() => handleWhatsAppSendMessage('Hi, I want ID.')}
                    className="px-3 py-1.5 bg-[#e8f5e9] text-[#128C7E] text-sm rounded-full border border-[#25D366] hover:bg-[#d4edda] transition-colors"
                  >
                    Hi, I want ID
                  </button>
                </>
              )}
            </div>
          </div>
        )}
        
        <WhatsAppInput
          onSendMessage={handleWhatsAppSendMessage}
          onSendMedia={handleMediaSelected}
          onOpenMediaPicker={() => setShowMediaPicker(true)}
          onTyping={handleTypingIndicator}
          placeholder="Type a message..."
          disabled={false}
          replyingTo={replyingTo}
          onCancelReply={cancelReply}
          userId={user?.id}
          userRole="customer"
        />
      </div>

      {/* Long Press Menu */}
      {longPressMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setLongPressMenu(null)} />
          <div 
            className="fixed bg-white rounded-lg shadow-xl py-2 z-50"
            style={{ 
              top: Math.max(10, Math.min(longPressMenu.y, window.innerHeight - 300)),
              left: (() => {
                const menuWidth = 180;
                const x = longPressMenu.x;
                // For own messages: place menu so its RIGHT edge is at bubble's LEFT edge
                if (longPressMenu.isOwn) {
                  const menuLeft = x - menuWidth;
                  // Ensure menu doesn't go off the left edge of the screen
                  return Math.max(10, menuLeft);
                }
                // For received messages: place at click position, handle overflow
                if (x + menuWidth > window.innerWidth - 10) {
                  return Math.max(10, x - menuWidth);
                }
                return Math.max(10, x);
              })()
            }}
          >
            <button
              onClick={() => {
                const msg = messages.find(m => m.id === longPressMenu.messageId);
                if (msg) handleCopyMessage(msg.text);
              }}
              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
            >
              <Copy size={20} />
              Copy
            </button>
            
            {/* Reply Button */}
            <button
              onClick={() => {
                const msg = messages.find(m => m.id === longPressMenu.messageId);
                if (msg) handleReplyMessage(msg);
              }}
              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
            >
              <ArrowBendUpLeft size={20} />
              Reply
            </button>

            {/* Star Button */}
            <button
              onClick={async () => {
                try {
                  const res = await axios.post(`${API}/chat/message/${longPressMenu.messageId}/star?user_id=${user.id}`);
                  setMessages(prev => prev.map(m => {
                    if (m.id === longPressMenu.messageId) {
                      const starred = m.starred_by || [];
                      return res.data.starred 
                        ? { ...m, starred_by: [...starred, user.id] }
                        : { ...m, starred_by: starred.filter(id => id !== user.id) };
                    }
                    return m;
                  }));
                  toast(res.data.starred ? 'Message starred' : 'Star removed', { duration: 1500 });
                } catch (err) { toast.error('Failed to star message'); }
                setLongPressMenu(null);
              }}
              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
            >
              <Star size={20} weight={(() => { const msg = messages.find(m => m.id === longPressMenu.messageId); return msg?.starred_by?.includes(user.id) ? 'fill' : 'regular'; })()} className={(() => { const msg = messages.find(m => m.id === longPressMenu.messageId); return msg?.starred_by?.includes(user.id) ? 'text-yellow-500' : ''; })()} />
              {(() => { const msg = messages.find(m => m.id === longPressMenu.messageId); return msg?.starred_by?.includes(user.id) ? 'Unstar' : 'Star'; })()}
            </button>

            <button
              onClick={() => handleDeleteClick(longPressMenu.messageId)}
              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-red-600"
            >
              <Trash size={20} />
              Delete
            </button>
            {/* Pin Button */}
            <button
              onClick={async () => {
                try {
                  const isPinned = pinnedMessages.some(m => m.id === longPressMenu.messageId);
                  if (isPinned) {
                    await axios.delete(`${API}/chat/room/${roomId}/pin/${longPressMenu.messageId}`);
                    setPinnedMessages(prev => prev.filter(m => m.id !== longPressMenu.messageId));
                    toast('Message unpinned', { duration: 1500 });
                    setLongPressMenu(null);
                    return;
                  } else if (pinnedMessages.length >= 2) {
                    toast.error('Maximum 2 pinned messages — unpin one first');
                    setLongPressMenu(null);
                    return;
                  } else {
                    setPinDurationTarget(longPressMenu.messageId);
                    setLongPressMenu(null);
                    return;
                  }
                } catch (err) { toast.error('Failed to pin message'); setLongPressMenu(null); }
              }}
              className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 text-blue-600"
              data-testid="pin-message-btn"
            >
              <PushPin size={20} />
              {pinnedMessages.some(m => m.id === longPressMenu.messageId) ? 'Unpin' : 'Pin'}
            </button>
          </div>
        </>
      )}

      {pinDurationTarget && (
        <PinDurationPicker
          onClose={() => setPinDurationTarget(null)}
          onPick={async (duration) => {
            try {
              await axios.post(`${API}/chat/room/${roomId}/pin`, { message_id: pinDurationTarget, user_id: user.id, duration });
              const msg = messages.find(m => m.id === pinDurationTarget);
              if (msg) setPinnedMessages(prev => [...prev, msg]);
              toast('Message pinned', { duration: 1500 });
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Failed to pin message');
            }
            setPinDurationTarget(null);
          }}
        />
      )}

      {/* Delete Modal - Simple "Delete" for Customer */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Delete Message</h3>
            <p className="text-gray-600 mb-6">This will delete the message for you only.</p>
            <div className="space-y-2">
              <button
                onClick={handleDeleteMessage}
                className="w-full px-4 py-3 bg-red-100 hover:bg-red-200 rounded-lg text-red-600 font-semibold"
              >
                Delete
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setMessageToDelete(null);
                }}
                className="w-full px-4 py-3 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Support Menu Modal */}
      {showSupportMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Support Options</h3>
              <button
                onClick={() => setShowSupportMenu(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} weight="bold" />
              </button>
            </div>
            
            <div className="space-y-3">
              {/* Chat Support - Hide for inquiry guests */}
              {!user?.is_inquiry && (
                <button
                  onClick={() => {
                    setShowSupportMenu(false);
                    // Navigate to support chat
                    navigate('/customer-chat?type=support');
                  }}
                  className="w-full px-4 py-3 bg-purple-50 hover:bg-purple-100 rounded-lg flex items-center gap-3 transition-colors border border-purple-200"
                >
                  <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path>
                  </svg>
                  <div className="text-left flex-1">
                    <p className="font-semibold text-gray-800">Chat Support</p>
                    <p className="text-sm text-gray-600">Message us directly</p>
                  </div>
                </button>
              )}

              {/* Call Support */}
              {supportInfo.phone && (
                <button
                  onClick={() => {
                    window.location.href = `tel:${supportInfo.phone}`;
                    setShowSupportMenu(false);
                  }}
                  className="w-full px-4 py-3 bg-blue-50 hover:bg-blue-100 rounded-lg flex items-center gap-3 transition-colors border border-blue-200"
                >
                  <Phone size={24} weight="fill" className="text-blue-600" />
                  <div className="text-left flex-1">
                    <p className="font-semibold text-gray-800">Call Support</p>
                    <p className="text-sm text-gray-600">{supportInfo.phone}</p>
                  </div>
                </button>
              )}
              
              {/* WhatsApp Support */}
              {supportInfo.whatsapp && (
                <button
                  onClick={() => {
                    const message = encodeURIComponent('Hello, I need support');
                    window.open(`https://wa.me/${supportInfo.whatsapp}?text=${message}`, '_blank');
                    setShowSupportMenu(false);
                  }}
                  className="w-full px-4 py-3 bg-green-50 hover:bg-green-100 rounded-lg flex items-center gap-3 transition-colors border border-green-200"
                >
                  <WhatsappLogo size={24} weight="fill" className="text-green-600 flex-shrink-0" />
                  <div className="text-left flex-1 min-w-0 overflow-hidden">
                    <p className="font-semibold text-gray-800">WhatsApp Support</p>
                    <p className="text-sm text-gray-600 truncate">{supportInfo.whatsapp}</p>
                  </div>
                </button>
              )}
              
              {/* Support Link */}
              {supportInfo.support_link && (
                <button
                  onClick={() => {
                    window.open(supportInfo.support_link, '_blank');
                    setShowSupportMenu(false);
                  }}
                  className="w-full px-4 py-3 bg-purple-50 hover:bg-purple-100 rounded-lg flex items-center gap-3 transition-colors border border-purple-200"
                >
                  <Link size={24} weight="bold" className="text-purple-600 flex-shrink-0" />
                  <div className="text-left flex-1 min-w-0 overflow-hidden">
                    <p className="font-semibold text-gray-800">Support Link</p>
                    <p className="text-sm text-gray-600 truncate">{supportInfo.support_link}</p>
                  </div>
                </button>
              )}
              
              {/* No support options configured */}
              {!supportInfo.phone && !supportInfo.whatsapp && !supportInfo.support_link && (
                <div className="text-center py-6 text-gray-500">
                  <p>No support options configured yet.</p>
                  <p className="text-sm mt-2">Please contact admin.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Viewer */}
      {showStatusViewer && (
        <StatusViewer
          customerId={user.id}
          onClose={() => setShowStatusViewer(false)}
        />
      )}

      {/* Media Picker */}
      {showMediaPicker && (
        <MediaPicker
          onMediaSelected={handleMediaSelected}
          onClose={() => setShowMediaPicker(false)}
          userRole="customer"
        />
      )}

      {/* Media Viewer - WhatsApp Style */}
      {showImagePreview && previewImageUrl && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col" data-testid="media-viewer">
          <div className="flex items-center justify-between p-4 text-white">
            <button onClick={closeImagePreview} className="p-2 hover:bg-white/10 rounded-full">
              <X size={24} weight="bold" />
            </button>
            <span className="text-sm font-medium">
              {allMediaUrls.length > 1 ? `${currentMediaIdx + 1} / ${allMediaUrls.length}` : 'Photo'}
            </span>
            <div className="w-10" />
          </div>
          <div className="flex-1 flex items-center justify-center px-4 relative">
            {allMediaUrls.length > 1 && currentMediaIdx > 0 && (
              <button onClick={() => { const i = currentMediaIdx - 1; setCurrentMediaIdx(i); setPreviewImageUrl(allMediaUrls[i]); }}
                className="absolute left-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white z-10">
                <ArrowLeft size={20} weight="bold" />
              </button>
            )}
            <img src={previewImageUrl} alt="Preview" className="max-w-full max-h-[70vh] object-contain" />
            {/* Per-photo caption */}
            {allMediaCaptions[currentMediaIdx] && (
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm px-4 py-2">
                <p className="text-white text-sm text-center">{allMediaCaptions[currentMediaIdx]}</p>
              </div>
            )}
            {allMediaUrls.length > 1 && currentMediaIdx < allMediaUrls.length - 1 && (
              <button onClick={() => { const i = currentMediaIdx + 1; setCurrentMediaIdx(i); setPreviewImageUrl(allMediaUrls[i]); }}
                className="absolute right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white z-10">
                <CaretDown size={20} weight="bold" className="rotate-[-90deg]" />
              </button>
            )}
          </div>
          <div className="flex items-center justify-around p-4 border-t border-white/10">
            <button onClick={() => { if(navigator.share) navigator.share({url: previewImageUrl}).catch(()=>{}); else { navigator.clipboard.writeText(previewImageUrl); toast('Link copied'); } }}
              className="flex flex-col items-center gap-1 text-white/70 hover:text-white">
              <ShareFat size={22} /><span className="text-[10px]">Forward</span>
            </button>
            <button onClick={closeImagePreview} className="flex flex-col items-center gap-1 text-white/70 hover:text-white">
              <ArrowLeft size={22} /><span className="text-[10px]">Back</span>
            </button>
            <a href={previewImageUrl} download="photo.jpg" target="_blank" rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 text-white/70 hover:text-white">
              <CaretDown size={22} weight="bold" /><span className="text-[10px]">Download</span>
            </a>
          </div>
        </div>
      )}


      {/* Audio Call Screen */}
      {activeCall && (
        <AudioCallScreen
          socket={socket}
          callId={activeCall.callId}
          callerId={activeCall.callerId}
          calleeId={activeCall.calleeId}
          callerName={activeCall.callerName}
          calleeName={activeCall.calleeName}
          callerProfilePic={activeCall.callerProfilePic}
          calleeProfilePic={activeCall.calleeProfilePic}
          isIncoming={activeCall.isIncoming}
          currentUserId={user?.id}
          currentUserRole="customer"
          isConference={activeCall.isConference || false}
          onClose={() => setActiveCall(null)}
          API={API}
        />
      )}

      {/* Incoming Call */}
      {incomingCall && !activeCall && (
        <div className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#1a3a2a] rounded-2xl p-8 max-w-xs w-full text-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00a884] to-[#008069] mx-auto mb-4 flex items-center justify-center">
              <span className="text-3xl font-bold text-white">{(incomingCall.callerName || 'U')[0]?.toUpperCase()}</span>
            </div>
            <h3 className="text-xl font-bold text-white mb-1">{incomingCall.callerName}</h3>
            <p className="text-white/50 text-sm mb-6">
              {incomingCall.isConference 
                ? `Conference call (${incomingCall.participantNames?.join(', ')})` 
                : 'Incoming audio call...'}
            </p>
            <div className="flex justify-around">
              <button onClick={() => { socket.emit('call_reject', { call_id: incomingCall.callId }); setIncomingCall(null); }}
                className="w-14 h-14 rounded-full bg-red-500 flex items-center justify-center">
                <Phone size={24} weight="fill" className="text-white rotate-[135deg]" />
              </button>
              <button onClick={() => {
                setActiveCall({ callId: incomingCall.callId, callerId: incomingCall.callerId, calleeId: user?.id,
                  callerName: incomingCall.callerName, calleeName: user?.username, isIncoming: true,
                  isConference: incomingCall.isConference || false });
                setIncomingCall(null);
              }} className="w-14 h-14 rounded-full bg-[#00a884] flex items-center justify-center">
                <Phone size={24} weight="fill" className="text-white" />
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Client Withdraw / Deposit History Modal (Task B) */}
      <ClientHistoryModal
        open={!!historyModal}
        kind={historyModal}
        customerId={user?.id}
        onClose={() => setHistoryModal(null)}
      />
    </div>
  );
};

export default CustomerChat;
